<template>
  <div class="subgraph-tab">
    <!-- Loading -->
    <div v-if="loadingClaims" class="loading-state">
      <div style="display: flex; gap: 8px; justify-content: center; margin-bottom: 16px;">
        <div v-for="n in 3" :key="'sk-btn-' + n" style="height: 28px; border-radius: 4px; background: #e5e7eb;" :style="{ width: [72, 60, 68][n - 1] + 'px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }"></div>
      </div>
      <div style="height: 16px; width: 200px; margin: 0 auto 12px; background: #e5e7eb; border-radius: 4px; animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;"></div>
      <div style="display: flex; gap: 12px; justify-content: center;">
        <div v-for="n in 4" :key="'sk-stat-' + n" style="height: 12px; width: 50px; background: #f3f4f6; border-radius: 4px; animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;"></div>
      </div>
    </div>
    <div v-if="error" class="error-banner">{{ error }}</div>

    <!-- Body -->
    <div v-show="!loadingClaims && (claims.length || (nodes.Observation || []).length || (nodes.MedicationStatement || []).length || (nodes.Encounter || []).length || (nodes.Appointment || []).length || (nodes.ADTEpisode || []).length)" class="map-body">


      <!-- Main content area -->
      <div class="main-area">
        <!-- View toggle + axis controls (above swimlanes) -->
        <div class="top-controls">
          <div class="sidebar-view-toggle inline-toggle">
            <button
              class="view-btn" :class="{ active: activeView === 'timeline' }"
              @click="activeView = 'timeline'"
            ><i class="fa fa-timeline" /> Timeline</button>
            <button
              class="view-btn" :class="{ active: activeView === 'graph' }"
              @click="activeView = 'graph'"
            ><i class="fa fa-diagram-project" /> Graph</button>
            <button
              class="view-btn" :class="{ active: activeView === 'claims' }"
              @click="activeView = 'claims'"
            ><i class="fa fa-file-invoice-dollar" /> Claims</button>
          </div>
          <div v-if="claims.length" class="top-stats">
            {{ claims.length }} claims · {{ Object.keys(nodeIndex.Practitioner || {}).length }} providers · {{ Object.keys(nodeIndex.Condition || {}).length }} dx
            <template v-if="(nodes.Encounter || []).length"> · {{ (nodes.Encounter || []).length }} enc</template>
            <template v-if="labCount"> · {{ labCount }} labs</template>
            <template v-if="(nodes.MedicationStatement || []).length"> · {{ (nodes.MedicationStatement || []).length }} meds</template>
          </div>
          <template v-if="activeView === 'timeline' && claims.length">
            <div class="axis-controls">
              <div class="axis-group">
                <label class="axis-label">Y-axis</label>
                <div class="dimension-pills">
                  <button class="dim-pill" :class="{ active: yDimension === 'service_line' }" @click="yDimension = 'service_line'">Service Line</button>
                  <button class="dim-pill" :class="{ active: yDimension === 'provider' }" @click="yDimension = 'provider'">Provider</button>
                  <button class="dim-pill" :class="{ active: yDimension === 'condition' }" @click="yDimension = 'condition'">Condition</button>
                </div>
              </div>
              <div class="axis-group">
                <label class="axis-label">Color</label>
                <div class="dimension-pills">
                  <button class="dim-pill" :class="{ active: timelineColor === 'service_line' }" @click="timelineColor = 'service_line'">Service Line</button>
                  <button class="dim-pill" :class="{ active: timelineColor === 'provider' }" @click="timelineColor = 'provider'">Provider</button>
                  <button class="dim-pill" :class="{ active: timelineColor === 'condition' }" @click="timelineColor = 'condition'">Condition</button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Timeline view -->
        <template v-if="activeView === 'timeline'">
          <!-- Swimlanes -->
          <div class="timeline-area">
            <div ref="timelineContainer" class="timeline-container"></div>
          </div>

          <!-- Analyze Episodes button (below swimlanes) -->
          <div v-if="claims.length" class="below-swimlanes">
            <button
              v-if="!episodes.length && !loadingEpisodes"
              class="episode-btn" @click="analyzeEpisodes"
            ><i class="fa fa-brain" /> Analyze Episodes</button>
            <span v-if="loadingEpisodes" class="status-text loading">Analyzing…</span>
          </div>

          <!-- Episode list (below swimlanes) -->
          <div v-if="episodes.length" class="episodes-below">
            <div class="episodes-below-header">
              <span class="episodes-below-title">{{ enrichedEpisodes.length }} Episodes</span>
              <button v-if="selectedEpisodes.length" class="clear-btn" @click="clearEpisodes">Clear selection</button>
            </div>
            <div class="episodes-below-list">
              <div
                v-for="(ep, i) in enrichedEpisodes" :key="i"
                class="episode-card"
                :class="{ active: isEpisodeSelected(ep), dimmed: selectedEpisodes.length && !isEpisodeSelected(ep) }"
                @click="toggleEpisode(ep)"
                @mouseenter="hoveredEpisode = ep" @mouseleave="hoveredEpisode = null"
              >
                <div class="episode-card-top">
                  <span class="episode-dot" :style="{ background: ep._color }" />
                  <span class="episode-card-label">{{ ep.label }}</span>
                  <span class="episode-severity-badge" :class="ep.severity">{{ ep.severity }}</span>
                </div>
                <p class="episode-card-desc">{{ ep.description }}</p>
                <div class="episode-card-meta">{{ ep._claimCount }} claims · ${{ ep._totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 }) }} · {{ ep._dateRange }}</div>
              </div>
            </div>
          </div>
        </template>

        <!-- Graph view -->
        <template v-else-if="activeView === 'graph'">
          <!-- Layer toolbar (normal flow, above SVG) -->
          <div v-if="claims.length" class="layer-bar">
            <span class="layer-bar-label">Layers</span>
            <div
              v-for="(layer, idx) in graphLayers" :key="layer.id"
              class="layer-chip" :class="{ off: !layer.visible }"
              @click="toggleLayer(idx)"
            >
              <span class="layer-dot" :style="{ background: layer.color }" />
              <span>{{ layer.label }}</span>
              <span class="layer-chip-count">{{ layerCounts[layer.id] || 0 }}</span>
              <button class="arrow-btn" :disabled="idx === 0" @click.stop="moveLayer(idx, -1)"><i class="fa fa-chevron-up" /></button>
              <button class="arrow-btn" :disabled="idx === graphLayers.length - 1" @click.stop="moveLayer(idx, 1)"><i class="fa fa-chevron-down" /></button>
            </div>
            <div class="layer-bar-sep" />
            <div class="layer-chip" :class="{ off: !sizeByPaid }" @click="sizeByPaid = !sizeByPaid">
              <i class="fa fa-dollar-sign" />
              <span>Size by cost</span>
            </div>
          </div>
          <div class="graph-view">
            <div ref="graphContainer" class="graph-container"></div>
          </div>
        </template>

        <!-- Claims view — aggregated line items: lane -> RBCS subcategory -> code -->
        <template v-else-if="activeView === 'claims'">
          <div class="research-view">
            <div ref="researchContainer" class="research-container"></div>
          </div>
        </template>
      </div>

      <!-- Claim Drawer -->
      <transition name="drawer">
        <div v-if="selectedClaim" class="claim-drawer">
          <div class="drawer-header">
            <div>
              <span class="drawer-type-badge" :style="{ background: selectedClaim._color }">{{ selectedClaim.CLM_TYPE_LABEL || selectedClaim.CLM_TYPE_CD }}</span>
              <span class="drawer-date">{{ selectedClaim.CLM_FROM_DT }}</span>
            </div>
            <button class="drawer-close" @click="selectedClaim = null">✕</button>
          </div>
          <div class="drawer-content">
            <div class="drawer-section">
              <div class="drawer-row"><span class="drawer-label">Claim ID</span><span class="drawer-value mono sensitive" data-spoof-kind="identifier">{{ selectedClaim.CLM_ID }}</span></div>
              <div class="drawer-row"><span class="drawer-label">Paid</span><span class="drawer-value">${{ Number(selectedClaim.CLM_PMT_AMT || 0).toLocaleString() }}</span></div>
              <div v-if="selectedClaim.DGNS_DRG_CD && selectedClaim.DGNS_DRG_CD !== '0000'" class="drawer-row">
                <span class="drawer-label">DRG</span><span class="drawer-value mono">{{ selectedClaim.DGNS_DRG_CD }}</span>
              </div>
            </div>

            <!-- Providers (from edges + node index) -->
            <div v-if="claimProviders.length" class="drawer-section">
              <h4>Providers</h4>
              <div v-for="p in claimProviders" :key="p.NPI + p.role" class="drawer-card compact">
                <span class="dx-code">{{ p.role }}</span>
                <span class="dx-desc sensitive">{{ p.PROVIDER_NAME }} — {{ p.SPECIALTY }}</span>
              </div>
            </div>

            <!-- Organizations -->
            <div v-if="claimOrgs.length" class="drawer-section">
              <h4>Facilities</h4>
              <div v-for="o in claimOrgs" :key="o.ORG_ID + o.role" class="drawer-card compact">
                <span class="dx-code">{{ o.role }}</span>
                <span class="dx-desc sensitive">{{ o.FACILITY_NAME }}</span>
              </div>
            </div>

            <!-- Diagnoses (from edges + node index) -->
            <div v-if="claimDiagnoses.length" class="drawer-section">
              <h4>Diagnoses</h4>
              <div v-for="dx in claimDiagnoses" :key="dx.CODE" class="drawer-card compact">
                <span class="dx-code">{{ dx.CODE }}</span>
                <span class="dx-desc">{{ dx.SHORT_DESCRIPTION }}</span>
              </div>
            </div>

            <!-- Procedures -->
            <div v-if="claimHcpcs.length" class="drawer-section">
              <h4>Procedures</h4>
              <div v-for="h in claimHcpcs" :key="h.CODE + (h.LINE_NUM || '')" class="drawer-card">
                <div class="card-title">{{ h.CODE }}
                  <span v-if="h.ALLOWED_AMT && Number(h.ALLOWED_AMT) > 0" class="item-amt">${{ Number(h.ALLOWED_AMT).toLocaleString() }}</span>
                </div>
                <div class="card-subtitle" v-if="h.DESCRIPTION">{{ h.DESCRIPTION }}</div>
              </div>
            </div>

            <!-- Drugs — lead with the drug name (NDC→name crosswalk), fall back
                 to the raw NDC code only when the directory has no match. -->
            <div v-if="claimNdc.length" class="drawer-section">
              <h4>Drugs</h4>
              <div v-for="n in claimNdc" :key="n.CODE + (n.LINE_NUM || '')" class="drawer-card">
                <div class="card-title">{{ n.DESCRIPTION || n.CODE }}
                  <span v-if="n.ALLOWED_AMT && Number(n.ALLOWED_AMT) > 0" class="item-amt">${{ Number(n.ALLOWED_AMT).toLocaleString() }}</span>
                </div>
                <div class="card-subtitle mono" v-if="n.DESCRIPTION">NDC {{ n.CODE }}</div>
              </div>
            </div>

            <!-- Revenue Centers -->
            <div v-if="claimRevCtr.length" class="drawer-section">
              <h4>Revenue Centers</h4>
              <div v-for="rc in claimRevCtr" :key="rc.CODE + (rc.LINE_NUM || '')" class="drawer-card compact">
                <span class="dx-code">{{ rc.CODE }}</span>
                <span class="dx-desc">{{ rc.DESCRIPTION }}</span>
                <span v-if="rc.COVERED_AMT && Number(rc.COVERED_AMT) > 0" class="item-amt">${{ Number(rc.COVERED_AMT).toLocaleString() }}</span>
              </div>
            </div>

            <!-- Episodes this claim belongs to -->
            <div v-if="claimEpisodes.length" class="drawer-section">
              <h4>Episodes</h4>
              <div v-for="ep in claimEpisodes" :key="ep.label" class="drawer-card compact">
                <span class="ep-dot" :style="{ background: ep._color }" />
                <span class="dx-desc">{{ ep.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Clinical Item Drawer -->
      <transition name="drawer">
        <div v-if="selectedClinicalItem" class="claim-drawer">
          <div class="drawer-header">
            <div>
              <span class="drawer-type-badge" :style="{ background: selectedClinicalItem._color }">{{ selectedClinicalItem._label }}</span>
              <span class="drawer-date">{{ selectedClinicalItem._date ? new Date(selectedClinicalItem._date).toLocaleDateString() : '' }}</span>
            </div>
            <button class="drawer-close" @click="selectedClinicalItem = null">✕</button>
          </div>
          <div class="drawer-content">
            <!-- Encounter (with linked vitals/assessments/labs) -->
            <template v-if="selectedClinicalItem._type === 'Encounter'">
              <div class="drawer-section">
                <div v-if="selectedClinicalItem.ENCOUNTER_TYPE" class="drawer-row"><span class="drawer-label">Type</span><span class="drawer-value">{{ selectedClinicalItem.ENCOUNTER_TYPE }}</span></div>
                <div v-if="selectedClinicalItem.ENCOUNTER_CLASS" class="drawer-row"><span class="drawer-label">Class</span><span class="drawer-value">{{ selectedClinicalItem.ENCOUNTER_CLASS }}</span></div>
                <div v-if="selectedClinicalItem.CLINICIAN" class="drawer-row"><span class="drawer-label">Clinician</span><span class="drawer-value sensitive">{{ selectedClinicalItem.CLINICIAN }}</span></div>
                <div v-if="selectedClinicalItem.LOCATION" class="drawer-row"><span class="drawer-label">Location</span><span class="drawer-value">{{ selectedClinicalItem.LOCATION }}</span></div>
                <div v-if="selectedClinicalItem.STATUS" class="drawer-row"><span class="drawer-label">Status</span><span class="drawer-value">{{ selectedClinicalItem.STATUS }}</span></div>
                <div v-if="selectedClinicalItem.SOURCE" class="drawer-row"><span class="drawer-label">Source</span><span class="drawer-value">{{ selectedClinicalItem.SOURCE }}</span></div>
              </div>
              <div v-if="selectedClinicalItem._vitals && selectedClinicalItem._vitals.length" class="drawer-section">
                <h4>Vitals ({{ selectedClinicalItem._vitals.length }})</h4>
                <div v-for="(v, i) in selectedClinicalItem._vitals" :key="'vit'+i" class="drawer-card compact">
                  <span class="dx-code">{{ v.CODE_DISPLAY || v.CODE }}</span>
                  <span class="dx-desc">{{ v.VALUE_STRING || v.VALUE_NUMERIC || '' }}{{ v.UNITS ? ' ' + v.UNITS : '' }}</span>
                </div>
              </div>
              <div v-if="selectedClinicalItem._assessments && selectedClinicalItem._assessments.length" class="drawer-section">
                <h4>Assessments ({{ selectedClinicalItem._assessments.length }})</h4>
                <div v-for="(a, i) in selectedClinicalItem._assessments" :key="'ass'+i" class="drawer-card compact">
                  <span class="dx-code">{{ a.CODE_DISPLAY || a.CODE }}</span>
                  <span class="dx-desc">{{ a.VALUE_STRING || '' }}</span>
                </div>
              </div>
              <div v-if="selectedClinicalItem._labs && selectedClinicalItem._labs.length" class="drawer-section">
                <h4>Labs ({{ selectedClinicalItem._labs.length }})</h4>
                <div v-for="(lab, i) in selectedClinicalItem._labs" :key="'lab'+i" class="drawer-card compact">
                  <span class="dx-code">{{ lab.CODE_DISPLAY || lab.CODE }}</span>
                  <span class="dx-desc">{{ lab.VALUE_NUMERIC ? lab.VALUE_NUMERIC + (lab.UNITS ? ' ' + lab.UNITS : '') : lab.VALUE_STRING || '' }}
                    <span v-if="lab.ABNORMAL_FLAG" style="color:#dc2626;font-weight:600"> ⚠ {{ lab.ABNORMAL_FLAG }}</span>
                  </span>
                </div>
              </div>
            </template>
            <!-- Standalone Lab -->
            <template v-if="selectedClinicalItem._type === 'Labs'">
              <div class="drawer-section">
                <div v-if="selectedClinicalItem.CODE_DISPLAY || selectedClinicalItem.CODE" class="drawer-row"><span class="drawer-label">Test</span><span class="drawer-value">{{ selectedClinicalItem.CODE_DISPLAY || selectedClinicalItem.CODE }}</span></div>
                <div v-if="selectedClinicalItem.VALUE_NUMERIC || selectedClinicalItem.VALUE_STRING" class="drawer-row"><span class="drawer-label">Result</span><span class="drawer-value">{{ selectedClinicalItem.VALUE_NUMERIC || '' }}{{ selectedClinicalItem.VALUE_STRING || '' }}{{ selectedClinicalItem.UNITS ? ' ' + selectedClinicalItem.UNITS : '' }}</span></div>
                <div v-if="selectedClinicalItem.ABNORMAL_FLAG" class="drawer-row"><span class="drawer-label">Flag</span><span class="drawer-value" style="color:#dc2626;font-weight:600">⚠ {{ selectedClinicalItem.ABNORMAL_FLAG }}</span></div>
                <div v-if="selectedClinicalItem.SOURCE" class="drawer-row"><span class="drawer-label">Source</span><span class="drawer-value">{{ selectedClinicalItem.SOURCE }}</span></div>
              </div>
            </template>
            <!-- Medication -->
            <template v-if="selectedClinicalItem._type === 'Meds'">
              <div class="drawer-section">
                <div v-if="selectedClinicalItem.MED_DETAILS" class="drawer-row"><span class="drawer-label">Medication</span><span class="drawer-value">{{ selectedClinicalItem.MED_DETAILS }}</span></div>
                <div v-if="selectedClinicalItem.NDC" class="drawer-row"><span class="drawer-label">NDC</span><span class="drawer-value">{{ selectedClinicalItem.NDC }}</span></div>
                <div v-if="selectedClinicalItem.FREQUENCY" class="drawer-row"><span class="drawer-label">Frequency</span><span class="drawer-value">{{ selectedClinicalItem.FREQUENCY }}</span></div>
                <div v-if="selectedClinicalItem.STATUS" class="drawer-row"><span class="drawer-label">Status</span><span class="drawer-value">{{ selectedClinicalItem.STATUS }}</span></div>
                <div v-if="selectedClinicalItem.START_DATE" class="drawer-row"><span class="drawer-label">Started</span><span class="drawer-value">{{ selectedClinicalItem.START_DATE }}</span></div>
                <div v-if="selectedClinicalItem.STOP_DATE" class="drawer-row"><span class="drawer-label">Stopped</span><span class="drawer-value">{{ selectedClinicalItem.STOP_DATE }}</span></div>
                <div v-if="selectedClinicalItem.SOURCE" class="drawer-row"><span class="drawer-label">Source</span><span class="drawer-value">{{ selectedClinicalItem.SOURCE }}</span></div>
              </div>
            </template>
            <!-- Appointment -->
            <template v-if="selectedClinicalItem._type === 'Appointment'">
              <div class="drawer-section">
                <div v-if="selectedClinicalItem.APPT_TYPE" class="drawer-row"><span class="drawer-label">Type</span><span class="drawer-value">{{ selectedClinicalItem.APPT_TYPE }}</span></div>
                <div v-if="selectedClinicalItem.CLINICIAN" class="drawer-row"><span class="drawer-label">Clinician</span><span class="drawer-value sensitive">{{ selectedClinicalItem.CLINICIAN }}</span></div>
                <div v-if="selectedClinicalItem.REASON" class="drawer-row"><span class="drawer-label">Reason</span><span class="drawer-value">{{ selectedClinicalItem.REASON }}</span></div>
                <div v-if="selectedClinicalItem.STATUS" class="drawer-row"><span class="drawer-label">Status</span><span class="drawer-value">{{ selectedClinicalItem.STATUS }}</span></div>
                <div v-if="selectedClinicalItem.DURATION" class="drawer-row"><span class="drawer-label">Duration</span><span class="drawer-value">{{ selectedClinicalItem.DURATION }} min</span></div>
                <div v-if="selectedClinicalItem.SOURCE" class="drawer-row"><span class="drawer-label">Source</span><span class="drawer-value">{{ selectedClinicalItem.SOURCE }}</span></div>
              </div>
            </template>
            <!-- ADT Episode -->
            <template v-if="selectedClinicalItem._type === 'ADTEpisode'">
              <div class="drawer-section">
                <div v-if="selectedClinicalItem.EPISODE_TYPE" class="drawer-row"><span class="drawer-label">Episode Type</span><span class="drawer-value">{{ selectedClinicalItem.EPISODE_TYPE }}</span></div>
                <div v-if="selectedClinicalItem.FACILITY" class="drawer-row"><span class="drawer-label">Facility</span><span class="drawer-value sensitive">{{ selectedClinicalItem.FACILITY }}</span></div>
                <div v-if="selectedClinicalItem.EPISODE_START" class="drawer-row"><span class="drawer-label">Start</span><span class="drawer-value">{{ selectedClinicalItem.EPISODE_START }}</span></div>
                <div v-if="selectedClinicalItem.EPISODE_END" class="drawer-row"><span class="drawer-label">End</span><span class="drawer-value">{{ selectedClinicalItem.EPISODE_END }}</span></div>
                <div v-if="selectedClinicalItem.DURATION_HOURS && selectedClinicalItem.DURATION_HOURS !== '0'" class="drawer-row"><span class="drawer-label">Duration</span><span class="drawer-value">{{ selectedClinicalItem.DURATION_HOURS }} hours</span></div>
                <div v-if="selectedClinicalItem.TOTAL_EVENTS" class="drawer-row"><span class="drawer-label">Events</span><span class="drawer-value">{{ selectedClinicalItem.TOTAL_EVENTS }}</span></div>
                <div v-if="selectedClinicalItem.ADMIT_SOURCE" class="drawer-row"><span class="drawer-label">Admit Source</span><span class="drawer-value">{{ selectedClinicalItem.ADMIT_SOURCE }}</span></div>
                <div v-if="selectedClinicalItem.DISCHARGE_DISPOSITION" class="drawer-row"><span class="drawer-label">Disposition</span><span class="drawer-value">{{ selectedClinicalItem.DISCHARGE_DISPOSITION }}</span></div>
                <div v-if="selectedClinicalItem.FIRST_ED_ARRIVAL" class="drawer-row"><span class="drawer-label">ED Arrival</span><span class="drawer-value">{{ selectedClinicalItem.FIRST_ED_ARRIVAL }}</span></div>
                <div v-if="selectedClinicalItem.FIRST_IP_ADMIT" class="drawer-row"><span class="drawer-label">IP Admit</span><span class="drawer-value">{{ selectedClinicalItem.FIRST_IP_ADMIT }}</span></div>
                <div v-if="selectedClinicalItem.FINAL_IP_DISCHARGE" class="drawer-row"><span class="drawer-label">IP Discharge</span><span class="drawer-value">{{ selectedClinicalItem.FINAL_IP_DISCHARGE }}</span></div>
                <div v-if="selectedClinicalItem.FINAL_ED_DISCHARGE" class="drawer-row"><span class="drawer-label">ED Discharge</span><span class="drawer-value">{{ selectedClinicalItem.FINAL_ED_DISCHARGE }}</span></div>
                <div v-if="selectedClinicalItem.DX_CODES" class="drawer-row"><span class="drawer-label">Diagnoses</span><span class="drawer-value">{{ selectedClinicalItem.DX_CODES }}</span></div>
              </div>
            </template>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import * as d3 from 'd3'
import abortable from '@/mixins/abortable'

const EPISODE_PALETTE = [
  '#7c3aed','#2563eb','#059669','#d97706','#dc2626','#0891b2','#7c2d12',
  '#4f46e5','#0d9488','#b91c1c','#ca8a04','#6d28d9','#0284c7','#be185d',
  '#15803d','#9333ea','#c2410c','#1d4ed8','#047857','#e11d48',
]

const LANE_MAP = {
  '60': '_INSTITUTIONAL', '20': '_INSTITUTIONAL', '61': '_INSTITUTIONAL',
  '40': '_OUTPATIENT', '10': '_OUTPATIENT',
  '71': '_PART_B', '72': '_PART_B',
  'D': 'Part D (Rx)',
  '81': 'SNF', '82': 'HHA', '50': 'Hospice', '30': 'DME',
  '03': 'SNF',
}
const RBCS_LANE_MAP = {
  'E&M': 'E&M',
  'Procedure': 'Procedure/Treatment',
  'Treatment': 'Procedure/Treatment',
  'Test': 'Test/Lab',
  'Imaging': 'Imaging',
}
const REV_LANE_MAP = {
  'Emergency Room': 'ED',
  'Operating Room Services': 'OP Surgery',
  'Operating Room Recovery': 'OP Surgery',
  'Recovery Room': 'OP Surgery',
  'Anesthesia': 'OP Surgery',
  'Laboratory': 'OP Diagnostic',
  'Radiology Diagnostic': 'OP Diagnostic',
  'Radiology Therapeutic': 'OP Diagnostic',
  'CT Scan': 'OP Diagnostic',
  'MRI': 'OP Diagnostic',
  'Nuclear Medicine': 'OP Diagnostic',
  'Other Imaging Services': 'OP Diagnostic',
  'EKG': 'OP Diagnostic',
  'EEG': 'OP Diagnostic',
  'Cardiology': 'OP Diagnostic',
  'Pulmonary Function': 'OP Diagnostic',
  'Other Diagnostic Services': 'OP Diagnostic',
  'Gastrointestinal Services': 'OP Diagnostic',
  'Physical Therapy': 'OP Therapy',
  'Occupational Therapy': 'OP Therapy',
  'Respiratory Services': 'OP Therapy',
  'Behavioral Health Services': 'OP Therapy',
  'Behavioral Health Treatments': 'OP Therapy',
  'Speech-Language Pathology': 'OP Therapy',
}
const LANE_COLORS = {
  'Inpatient': '#dc2626', 'ED': '#f97316', 'SNF': '#9333ea', 'Hospice': '#6b7280', 'HHA': '#0891b2',
  'DME': '#ca8a04', 'Part D (Rx)': '#7c3aed',
  'E&M': '#059669', 'Procedure/Treatment': '#be185d', 'Test/Lab': '#0891b2', 'Imaging': '#6d28d9',
  'OP Surgery': '#dc2626', 'OP Diagnostic': '#2563eb', 'OP Therapy': '#0d9488', 'OP Other': '#78716c',
}
function _dominantLane(claim, edgeIndex, nodeIndex, edgeType, nodeType, field, laneMap) {
  if (!edgeIndex || !nodeIndex) return null
  const itemEdges = (edgeIndex.byFrom['EOB_ITEM'] || {})[claim.CLM_ID] || []
  const counts = {}
  for (const ie of itemEdges) {
    const item = nodeIndex.Item?.[ie.to]
    if (!item) continue
    const codeEdges = (edgeIndex.byFrom[edgeType] || {})[item._key] || []
    for (const ce of codeEdges) {
      const cNode = nodeIndex[nodeType]?.[ce.to]
      if (!cNode) continue
      const lane = laneMap[cNode[field]]
      if (lane) counts[lane] = (counts[lane] || 0) + 1
    }
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
  return best ? best[0] : null
}
function laneKey(code, claim, edgeIndex, nodeIndex) {
  if (claim && claim._isED) return 'ED'
  const base = LANE_MAP[code]
  if (base === '_INSTITUTIONAL') {
    if (claim && claim.CLM_BILL_FAC_TYPE_CD === '2') return 'SNF'
    return 'Inpatient'
  }
  if (base === '_PART_B') {
    return _dominantLane(claim, edgeIndex, nodeIndex, 'ITEM_HCPCS', 'HCPCS_CODE', 'RBCS_CAT_DESC', RBCS_LANE_MAP) || 'E&M'
  }
  if (base === '_OUTPATIENT') {
    const revLane = _dominantLane(claim, edgeIndex, nodeIndex, 'ITEM_REV_CTR', 'REV_CTR_CODE', 'CATEGORY', REV_LANE_MAP)
    return revLane || 'OP Other'
  }
  return base || `Other (${code})`
}

// Auto-assign colors for any dimension that doesn't have a fixed palette
const DIM_PALETTE = [
  '#2563eb','#dc2626','#059669','#d97706','#7c3aed','#0891b2','#ca8a04',
  '#9333ea','#be185d','#0d9488','#4f46e5','#15803d','#c2410c','#b91c1c',
  '#1d4ed8','#047857','#e11d48','#6d28d9','#0284c7','#7c2d12',
]



export default {
  name: 'Patient_Subgraph',
  mixins: [abortable],
  props: {
    patientIdentifier: { type: String, required: true },
  },
  data() {
    return {
      // Raw graph
      nodes: {},
      edges: [],
      // Derived
      claims: [],
      episodes: [],
      // UI
      patientName: '',
      error: null,
      loadingClaims: false,
      loadingEpisodes: false,
      selectedClaim: null,
      selectedClinicalItem: null,
      selectedEpisodes: [],
      hoveredEpisode: null,
      sidebarOpen: true,
      activeView: 'timeline',
      sizeByPaid: false,
      // Graph view layers (Patient is always center, not in this list)
      graphLayers: [
        { id: 'Practitioner', label: 'Providers', color: '#2563eb', visible: true },
        { id: 'Organization', label: 'Facilities', color: '#0891b2', visible: true },
        { id: 'Claim', label: 'Claims', color: '#6b7280', visible: true },
        { id: 'Condition', label: 'Diagnoses', color: '#d97706', visible: true },
        { id: 'Item', label: 'Items', color: '#7c3aed', visible: true },
      ],
      // Axis config
      yDimension: 'service_line',
      colorDimension: null,
      timelineColor: 'service_line',
    }
  },
  computed: {
    /** All available dimensions for axis configuration */
    availableDimensions() {
      const dims = [
        { id: 'service_line', label: 'Service Line' },
        { id: 'claim_type', label: 'Claim Type' },
        { id: 'provider', label: 'Provider' },
        { id: 'facility', label: 'Facility' },
        { id: 'drg', label: 'DRG' },
        { id: 'date', label: 'Date' },
      ]
      if (this.episodes.length) {
        dims.push({ id: 'episode', label: 'Episode' })
      }
      return dims
    },

    labCount() { return (this.nodes.Observation || []).filter(o => o.CATEGORY === 'laboratory').length },
    vitalCount() { return (this.nodes.Observation || []).filter(o => o.CATEGORY === 'vital-signs').length },

    /** Index every node type by its natural key for O(1) lookup */
    nodeIndex() {
      const idx = {}
      for (const [type, list] of Object.entries(this.nodes)) {
        const keyCol = { Patient: 'MBI', Claim: 'CLM_ID', Practitioner: 'NPI',
                         Organization: 'ORG_ID', Condition: 'CODE', Item: '_key',
                         HCPCS_CODE: 'CODE', NDC_CODE: 'CODE', REV_CTR_CODE: 'CODE',
                         Encounter: 'ENCOUNTER_KEY', Observation: '_key',
                         MedicationStatement: '_key', Appointment: '_key' }[type]
        const map = {}
        for (const n of list) map[n[keyCol]] = n
        idx[type] = map
      }
      return idx
    },

    /** Index edges by type + from/to for fast lookup */
    edgeIndex() {
      const byFrom = {}  // type → from_id → [edge]
      const byTo = {}    // type → to_id → [edge]
      for (const e of this.edges) {
        if (!byFrom[e.type]) byFrom[e.type] = {}
        if (!byTo[e.type]) byTo[e.type] = {}
        ;(byFrom[e.type][e.from] ||= []).push(e)
        ;(byTo[e.type][e.to] ||= []).push(e)
      }
      return { byFrom, byTo }
    },

    /** Providers for selected claim — join edges to node index */
    claimProviders() {
      if (!this.selectedClaim) return []
      const edges = (this.edgeIndex.byFrom['EOB_PRACTITIONER'] || {})[this.selectedClaim.CLM_ID] || []
      return edges.map(e => ({ ...(this.nodeIndex.Practitioner?.[e.to] || {}), role: e.ROLE || '' }))
    },

    claimOrgs() {
      if (!this.selectedClaim) return []
      const edges = (this.edgeIndex.byFrom['EOB_ORGANIZATION'] || {})[this.selectedClaim.CLM_ID] || []
      return edges.map(e => ({ ...(this.nodeIndex.Organization?.[e.to] || {}), role: e.ROLE || '' }))
    },

    claimDiagnoses() {
      if (!this.selectedClaim) return []
      const edges = (this.edgeIndex.byFrom['EOB_CONDITION'] || {})[this.selectedClaim.CLM_ID] || []
      return edges.map(e => this.nodeIndex.Condition?.[e.to]).filter(Boolean)
    },

    /** All items for the selected claim, via EOB_ITEM edges → Item nodeIndex */
    claimItems() {
      if (!this.selectedClaim) return []
      const edges = (this.edgeIndex.byFrom['EOB_ITEM'] || {})[this.selectedClaim.CLM_ID] || []
      return edges.map(e => this.nodeIndex.Item?.[e.to]).filter(Boolean)
    },

    claimHcpcs() {
      return this.claimItems.filter(i => (i.CODE_TYPE || '').trim() === 'HCPCS' && (i.CODE || '').trim()).map(i => {
        const codeEdges = (this.edgeIndex.byFrom['ITEM_HCPCS'] || {})[i._key] || []
        const codeNode = codeEdges.length ? this.nodeIndex.HCPCS_CODE?.[codeEdges[0].to] : null
        return codeNode ? { ...i, DESCRIPTION: codeNode.DESCRIPTION || i.DESCRIPTION } : i
      })
    },

    claimNdc() {
      return this.claimItems.filter(i => (i.CODE_TYPE || '').trim() === 'NDC').map(i => {
        const codeEdges = (this.edgeIndex.byFrom['ITEM_NDC'] || {})[i._key] || []
        const codeNode = codeEdges.length ? this.nodeIndex.NDC_CODE?.[codeEdges[0].to] : null
        return codeNode ? { ...i, DESCRIPTION: codeNode.DESCRIPTION || i.DESCRIPTION } : i
      })
    },

    claimRevCtr() {
      return this.claimItems.filter(i => (i.CODE_TYPE || '').trim() === 'REV_CTR').map(i => {
        const codeEdges = (this.edgeIndex.byFrom['ITEM_REV_CTR'] || {})[i._key] || []
        const codeNode = codeEdges.length ? this.nodeIndex.REV_CTR_CODE?.[codeEdges[0].to] : null
        return codeNode ? { ...i, DESCRIPTION: codeNode.DESCRIPTION || i.DESCRIPTION } : i
      })
    },

    claimEpisodes() {
      if (!this.selectedClaim) return []
      return this.enrichedEpisodes.filter(ep => ep._claimIdSet.has(this.selectedClaim.CLM_ID))
    },

    enrichedEpisodes() {
      const claimMap = this.nodeIndex.Claim || {}
      return this.episodes.map((ep, i) => {
        const epClaims = (ep.claim_ids || []).map(id => claimMap[id]).filter(Boolean)
          .sort((a, b) => (a.CLM_FROM_DT || '').localeCompare(b.CLM_FROM_DT || ''))
        const totalCost = epClaims.reduce((s, c) => s + (parseFloat(c.CLM_PMT_AMT) || 0), 0)
        const dates = epClaims.map(c => c.CLM_FROM_DT).filter(Boolean)
        return {
          ...ep,
          _color: EPISODE_PALETTE[i % EPISODE_PALETTE.length],
          _claims: epClaims,
          _claimCount: epClaims.length,
          _totalCost: totalCost,
          _dateRange: dates.length ? `${dates[0]} → ${dates[dates.length - 1]}` : '',
          _claimIdSet: new Set(ep.claim_ids || []),
          _encounterIdSet: new Set(ep.encounter_ids || []),
          _medicationIdSet: new Set(ep.medication_ids || []),
          _appointmentIdSet: new Set(ep.appointment_ids || []),
          _observationIdSet: new Set(ep.observation_ids || []),
          _adtIdSet: new Set(ep.adt_ids || []),
          _allNodeIds: new Set([
            ...(ep.claim_ids || []),
            ...(ep.encounter_ids || []),
            ...(ep.medication_ids || []),
            ...(ep.appointment_ids || []),
            ...(ep.observation_ids || []),
            ...(ep.adt_ids || []),
          ]),
        }
      })
    },

    /** Serialized layer state — watched to trigger graph re-render */
    graphLayerKey() {
      return this.graphLayers.map(l => `${l.id}:${l.visible}`).join(',') + `|cost:${this.sizeByPaid}`
    },

    /** Count of nodes per graph layer type */
    layerCounts() {
      return {
        Practitioner: Object.keys(this.nodeIndex.Practitioner || {}).length,
        Organization: Object.keys(this.nodeIndex.Organization || {}).length,
        Claim: (this.nodes.Claim || []).length,
        Condition: Object.keys(this.nodeIndex.Condition || {}).length,
        Item: Object.keys(this.nodeIndex.Item || {}).length,
      }
    },

    /** Map of episode label → {color, claim_ids set} for the episode dimension */
    episodeMap() {
      const map = {}
      for (const ep of this.enrichedEpisodes) {
        map[ep.label] = ep
      }
      return map
    },
  },
  watch: {
    patientIdentifier: {
      immediate: true,
      handler() {
        const signal = this._renewAbortSignal();
        this.loadSubgraph(signal);
      },
    },
    hoveredEpisode(ep) {
      if (ep) {
        this.highlightDots(ep._claimIdSet, ep._color, ep)
      } else if (this.selectedEpisodes.length) {
        this.applyEpisodeHighlight()
      } else {
        this.resetDots()
      }
    },
    yDimension() { this.renderTimeline() },
    timelineColor() { this.renderTimeline() },
    sidebarOpen() { this.$nextTick(() => { if (this.activeView === 'timeline') this.renderTimeline(); else this.renderGraph() }) },
    graphLayerKey() { if (this.activeView === 'graph') this.$nextTick(() => this.renderGraph()) },
    activeView(v) {
      if (v === 'timeline') this.$nextTick(() => this.renderTimeline())
      if (v === 'graph') this.$nextTick(() => this.renderGraph())
      if (v === 'claims') this.$nextTick(() => this.renderTreemap())
    },
  },
  mounted() {
    this._resizeObserver = new ResizeObserver(() => {
      if (this.claims.length && !this.loadingClaims) this.renderTimeline()
    })
    if (this.$refs.timelineContainer) this._resizeObserver.observe(this.$refs.timelineContainer)
  },
  beforeUnmount() {
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this._graphSim) { this._graphSim.stop(); this._graphSim = null }
  },
  methods: {
    laneKey,
    LANE_COLORS,

    toggleLayer(idx) {
      const layers = this.graphLayers.map((l, i) =>
        i === idx ? { ...l, visible: !l.visible } : { ...l }
      )
      this.graphLayers = layers
    },

    moveLayer(idx, dir) {
      const target = idx + dir
      if (target < 0 || target >= this.graphLayers.length) return
      const layers = this.graphLayers.map(l => ({ ...l }))
      const tmp = layers[idx]
      layers[idx] = layers[target]
      layers[target] = tmp
      this.graphLayers = layers
    },

    /**
     * Resolve a dimension value for a claim. Returns array of strings (one claim
     * can map to multiple values for edge-based dimensions like provider).
     */
    resolveDimension(claim, dimId) {
      switch (dimId) {
        case 'service_line': {
          const lanes = [laneKey(claim.CLM_TYPE_CD, claim, this.edgeIndex, this.nodeIndex)]
          // Inpatient claims admitted from ED → duplicate into ED lane
          if (claim.CLM_TYPE_CD === '60' && claim.CLM_ADMSN_TYPE_CD === '1') {
            lanes.push('ED')
          }
          return lanes
        }
        case 'claim_type':
          return [claim.CLM_TYPE_LABEL || claim.CLM_TYPE_CD || 'Unknown']
        case 'provider': {
          const edges = (this.edgeIndex.byFrom['EOB_PRACTITIONER'] || {})[claim.CLM_ID] || []
          const names = edges.map(e => {
            const prac = this.nodeIndex.Practitioner?.[e.to]
            return prac ? (prac.PROVIDER_NAME || e.to) : e.to
          })
          return names.length ? names : ['No Provider']
        }
        case 'facility': {
          const edges = (this.edgeIndex.byFrom['EOB_ORGANIZATION'] || {})[claim.CLM_ID] || []
          const names = edges.map(e => {
            const org = this.nodeIndex.Organization?.[e.to]
            return org ? (org.FACILITY_NAME || e.to) : e.to
          })
          return names.length ? names : ['No Facility']
        }
        case 'condition': {
          const edges = (this.edgeIndex.byFrom['EOB_CONDITION'] || {})[claim.CLM_ID] || []
          const codes = edges.map(e => {
            const cond = this.nodeIndex.Condition?.[e.to]
            return cond ? `${cond.CODE} ${cond.SHORT_DESCRIPTION || ''}`.trim() : e.to
          })
          return codes.length ? codes : ['No Dx']
        }
        case 'drg':
          return [(claim.DGNS_DRG_CD && claim.DGNS_DRG_CD !== '0000') ? `DRG ${claim.DGNS_DRG_CD}` : 'No DRG']
        case 'episode': {
          const eps = this.enrichedEpisodes.filter(ep => ep._claimIdSet.has(claim.CLM_ID))
          return eps.length ? eps.map(ep => ep.label) : ['Ungrouped']
        }
        case 'date':
          return [claim.CLM_FROM_DT || 'No Date']
        default:
          return ['—']
      }
    },

    /** Get lane keys for a claim based on current Y dimension */
    buildLaneKeys(claim) {
      return this.resolveDimension(claim, this.yDimension)
    },

    /** Get a color for a lane/dimension value */
    dimensionColor(dimId, value) {
      if (dimId === 'service_line') {
        return LANE_COLORS[value] || '#6b7280'
      }
      if (dimId === 'episode') {
        const ep = this.episodeMap[value]
        return ep ? ep._color : '#6b7280'
      }
      // Auto-assign from palette
      if (!this._colorCache) this._colorCache = {}
      if (!this._colorCache[dimId]) this._colorCache[dimId] = {}
      const cache = this._colorCache[dimId]
      if (!cache[value]) {
        cache[value] = DIM_PALETTE[Object.keys(cache).length % DIM_PALETTE.length]
      }
      return cache[value]
    },

    async loadSubgraph(signal) {
      const mbi = this.patientIdentifier
      if (!mbi) return
      this.loadingClaims = true
      this.error = null
      this.claims = []
      this.episodes = []
      this.nodes = {}
      this.edges = []
      this._colorCache = {}
      try {
        const { data } = await axios.get(`/api/graph/patient/${mbi}/subgraph/`, { signal })
        this.nodes = data.nodes
        this.edges = data.edges
        this.claims = (data.nodes.Claim || []).map(c => {
          if (c.CLM_TYPE_CD === '40') {
            const itemEdges = (data.edges || []).filter(e => e.type === 'EOB_ITEM' && e.from === c.CLM_ID)
            const itemKeys = new Set(itemEdges.map(e => e.to))
            const items = (data.nodes.Item || []).filter(i => itemKeys.has(i._key))
            if (items.some(i => (i.REV_CTR_CD || '').startsWith('045'))) {
              c._isED = true
            }
          }
          return c
        })
        const p = (data.nodes.Patient || [])[0] || {}
        this.patientName = `${p.FIRST_NAME || ''} ${p.LAST_NAME || ''}`.trim() || mbi
      } catch (e) {
        if (this._isAborted(e)) return
        this.error = e.response?.data?.error || e.message
      } finally {
        if (!signal?.aborted) {
          this.loadingClaims = false
          this.$nextTick(() => {
            if (this._resizeObserver && this.$refs.timelineContainer) {
              this._resizeObserver.observe(this.$refs.timelineContainer)
            }
            this.$nextTick(() => this.renderTimeline())
          })
        }
      }
    },

    async analyzeEpisodes() {
      const mbi = this.patientIdentifier
      if (!mbi) return
      this.loadingEpisodes = true
      try {
        const { data } = await axios.get(`/api/graph/patient/${mbi}/episodes/`)
        this.episodes = data.episodes || []
        this._colorCache = {}
      } catch (e) {
        this.error = e.response?.data?.error || e.message
      } finally {
        this.loadingEpisodes = false
      }
    },

    isEpisodeSelected(ep) {
      return this.selectedEpisodes.some(s => s.label === ep.label)
    },

    toggleEpisode(ep) {
      const idx = this.selectedEpisodes.findIndex(s => s.label === ep.label)
      if (idx >= 0) {
        this.selectedEpisodes.splice(idx, 1)
      } else {
        this.selectedEpisodes.push(ep)
      }
      this.applyEpisodeHighlight()
    },

    clearEpisodes() {
      this.selectedEpisodes = []
      this.resetDots()
    },

    /** Highlight union of all selected episode claim IDs */
    /** Build set of all graph node IDs connected to a set of claim IDs */
    _connectedGraphIds(claimIds) {
      const connected = new Set()
      connected.add(`Patient:${this.patientIdentifier}`)
      for (const cid of claimIds) {
        connected.add(`Claim:${cid}`)
      }
      for (const e of this.edges) {
        if (claimIds.has(e.from)) {
          const nodeType = { EOB_PRACTITIONER: 'Practitioner', EOB_ORGANIZATION: 'Organization',
            EOB_CONDITION: 'Condition', EOB_ITEM: 'Item' }[e.type]
          if (nodeType) connected.add(`${nodeType}:${e.to}`)
        }
      }
      return connected
    },

    /** Highlight claims matching a set of IDs on both timeline and graph */
    _highlightClaimIds(ids, colorFn, clinicalIds) {
      const tl = this.$refs.timelineContainer
      if (tl) {
        d3.select(tl).select('svg g').selectAll('.claim-dot')
          .transition().duration(200)
          .attr('opacity', d => ids.has(d.CLM_ID) ? 1 : 0.07)
          .attr('stroke', d => ids.has(d.CLM_ID) ? colorFn(d.CLM_ID) : '#fff')
          .attr('stroke-width', d => ids.has(d.CLM_ID) ? 2 : 0.5)
        // Clinical dots
        if (clinicalIds && clinicalIds.size) {
          d3.select(tl).select('svg g').selectAll('.clinical-dot')
            .transition().duration(200)
            .attr('opacity', function () { return clinicalIds.has(d3.select(this).attr('data-node-key')) ? 1 : 0.07 })
            .attr('stroke', function () { return clinicalIds.has(d3.select(this).attr('data-node-key')) ? '#111827' : '#fff' })
            .attr('stroke-width', function () { return clinicalIds.has(d3.select(this).attr('data-node-key')) ? 2 : 0.5 })
        } else {
          d3.select(tl).select('svg g').selectAll('.clinical-dot')
            .transition().duration(200)
            .attr('opacity', ids.size ? 0.07 : 0.7)
            .attr('stroke', '#fff').attr('stroke-width', 0.5)
        }
      }
      const gc = this.$refs.graphContainer
      if (gc) {
        const connected = this._connectedGraphIds(ids)
        d3.select(gc).selectAll('.graph-node')
          .transition().duration(200)
          .attr('opacity', d => connected.has(d.id) ? 1 : 0.07)
          .attr('stroke', d => {
            if (d.type === 'Claim' && ids.has(d.data?.CLM_ID)) return colorFn(d.data.CLM_ID)
            if (connected.has(d.id) && d.type !== 'Claim') return '#374151'
            return '#fff'
          })
          .attr('stroke-width', d => connected.has(d.id) ? 2 : 0.5)
        // Dim unrelated links
        d3.select(gc).selectAll('line')
          .transition().duration(200)
          .attr('stroke-opacity', d => {
            const sId = typeof d.source === 'object' ? d.source.id : d.source
            const tId = typeof d.target === 'object' ? d.target.id : d.target
            return connected.has(sId) && connected.has(tId) ? 0.6 : 0.03
          })
      }
    },

    _resetAllDots() {
      const tl = this.$refs.timelineContainer
      if (tl) {
        d3.select(tl).select('svg g').selectAll('.claim-dot')
          .transition().duration(200)
          .attr('opacity', 0.8).attr('stroke', '#fff').attr('stroke-width', 0.5)
        d3.select(tl).select('svg g').selectAll('.clinical-dot')
          .transition().duration(200)
          .attr('opacity', 0.7).attr('stroke', '#fff').attr('stroke-width', 0.5)
      }
      const gc = this.$refs.graphContainer
      if (gc) {
        d3.select(gc).selectAll('.graph-node')
          .transition().duration(200)
          .attr('opacity', d => d.type === 'Item' ? 0.5 : 0.85)
          .attr('stroke', '#fff')
          .attr('stroke-width', d => d.type === 'Patient' ? 2 : 0.5)
        d3.select(gc).selectAll('line')
          .transition().duration(200)
          .attr('stroke-opacity', 0.6)
      }
    },

    applyEpisodeHighlight() {
      if (!this.selectedEpisodes.length) {
        this._resetAllDots()
        return
      }
      const allClaimIds = new Set()
      const allClinicalIds = new Set()
      const claimColorMap = {}
      for (const ep of this.selectedEpisodes) {
        for (const id of ep._claimIdSet) { allClaimIds.add(id); claimColorMap[id] = ep._color }
        for (const id of (ep._allNodeIds || [])) { allClinicalIds.add(id) }
      }
      this._highlightClaimIds(allClaimIds, id => claimColorMap[id] || '#fff', allClinicalIds)
    },

    highlightDots(claimIds, color, ep) {
      const clinicalIds = ep ? ep._allNodeIds : null
      this._highlightClaimIds(claimIds, () => color, clinicalIds)
    },

    resetDots() {
      this._resetAllDots()
    },

    selectClaim(claim) {
      claim._color = claim._provColor || claim._dotColor || '#6b7280'
      this.selectedClaim = claim
      this.selectedClinicalItem = null
      const container = this.$refs.timelineContainer
      if (!container) return
      d3.select(container).select('svg g').selectAll('.claim-dot')
        .attr('stroke', d => d.CLM_ID === claim.CLM_ID ? '#111827' : '#fff')
        .attr('stroke-width', d => d.CLM_ID === claim.CLM_ID ? 2.5 : 0.5)
    },

    renderTimeline() {
      const container = this.$refs.timelineContainer
      if (!container) return
      container.innerHTML = ''
      const claims = this.claims
      if (!claims.length) return
      const self = this

      // Parse dates
      const dated = claims.map(c => ({
        ...c,
        _date: c.CLM_FROM_DT ? new Date(c.CLM_FROM_DT) : null,
      })).filter(d => d._date && !isNaN(d._date))
      if (!dated.length) return

      // Build lane keys for each claim (cross-product of Y dimensions)
      const claimLanes = []  // {claim, laneKey}
      for (const c of dated) {
        const keys = this.buildLaneKeys(c)
        for (const k of keys) {
          claimLanes.push({ ...c, _laneKey: k })
        }
      }

      // Collect unique lanes in order of first appearance, with count-based ordering
      const laneCounts = {}
      const laneFirstSeen = {}
      for (const cl of claimLanes) {
        laneCounts[cl._laneKey] = (laneCounts[cl._laneKey] || 0) + 1
        if (!(cl._laneKey in laneFirstSeen)) laneFirstSeen[cl._laneKey] = cl._date
      }
      // Sort: if single dim is service_line, use the canonical order with group headers.
      let lanes
      const groupHeaders = new Set()  // lanes that are group headers (rendered differently)
      if (this.yDimension === 'service_line') {
        const order = ['Inpatient', 'ED', 'SNF', 'Hospice', 'HHA', 'OP Surgery', 'OP Diagnostic', 'OP Therapy', 'OP Other', 'E&M', 'Procedure/Treatment', 'Test/Lab', 'Imaging', 'DME', 'Part D (Rx)']
        const inData = new Set(Object.keys(laneCounts))
        const flat = order.filter(l => inData.has(l))
        inData.forEach(l => { if (!flat.includes(l)) flat.push(l) })

        // Insert group headers for Outpatient and Part B sub-lanes
        const paLanes = ['Inpatient', 'ED', 'SNF', 'Hospice', 'HHA']
        const opLanes = ['OP Surgery', 'OP Diagnostic', 'OP Therapy', 'OP Other']
        const pbLanes = ['E&M', 'Procedure/Treatment', 'Test/Lab', 'Imaging']
        const hasPA = paLanes.some(l => flat.includes(l))
        const hasOP = opLanes.some(l => flat.includes(l))
        const hasPB = pbLanes.some(l => flat.includes(l))

        lanes = []
        for (const l of flat) {
          if (hasPA && paLanes.includes(l) && !groupHeaders.has('_hdr_Part A')) {
            lanes.push('_hdr_Part A')
            groupHeaders.add('_hdr_Part A')
          }
          if (hasOP && opLanes.includes(l) && !groupHeaders.has('_hdr_Outpatient')) {
            lanes.push('_hdr_Outpatient')
            groupHeaders.add('_hdr_Outpatient')
          }
          if (hasPB && pbLanes.includes(l) && !groupHeaders.has('_hdr_Part B')) {
            lanes.push('_hdr_Part B')
            groupHeaders.add('_hdr_Part B')
          }
          lanes.push(l)
        }
      } else {
        lanes = Object.keys(laneCounts).sort((a, b) => laneCounts[b] - laneCounts[a])
      }

      const isConditionAxis = this.yDimension === 'condition'
      const dataLaneCount = lanes.filter(l => !groupHeaders.has(l)).length
      const headerHeight = 20
      const margin = { top: 40, right: 20, bottom: 10, left: isConditionAxis ? 280 : Math.min(300, Math.max(90, d3.max(lanes, l => l.replace(/^_hdr_/, '').length) * 6.5 + 16)) }
      const width = container.clientWidth || 800
      const laneHeight = Math.max(28, Math.min(64, (Math.max(container.clientHeight, 500) - margin.top - margin.bottom - groupHeaders.size * headerHeight) / dataLaneCount))
      // Compute cumulative Y offsets per lane (headers are shorter)
      const laneY = {}
      let cumY = margin.top
      for (const lane of lanes) {
        laneY[lane] = cumY
        cumY += groupHeaders.has(lane) ? headerHeight : laneHeight
      }
      const height = cumY + margin.bottom

      const svg = d3.select(container).append('svg').attr('width', width).attr('height', height)
      const g = svg.append('g')

      // Collect ALL dates (claims + clinical) for a unified x-axis
      const allDates = [...dated.map(d => d._date)]
      const clinicalDateFields = { Observation: 'SERVICE_DATE', MedicationStatement: 'START_DATE', Appointment: 'APPOINTMENT_DATE', Encounter: 'ENCOUNTER_DATE', ADTEpisode: 'EPISODE_START' }
      for (const [nodeType, field] of Object.entries(clinicalDateFields)) {
        for (const item of (this.nodes[nodeType] || [])) {
          if (item[field]) { const d = new Date(item[field]); if (!isNaN(d)) allDates.push(d) }
        }
      }
      const x = d3.scaleTime().domain(d3.extent(allDates)).range([margin.left, width - margin.right])
      const y = (lane) => laneY[lane] + (groupHeaders.has(lane) ? headerHeight / 2 : laneHeight / 2)

      let stripeIdx = 0
      lanes.forEach((lane) => {
        if (groupHeaders.has(lane)) {
          // Group header — full-width background
          g.append('rect').attr('x', 0).attr('y', laneY[lane])
            .attr('width', width).attr('height', headerHeight)
            .attr('fill', '#f3f4f6')
          g.append('text').attr('x', 10).attr('y', y(lane)).attr('dy', 4)
            .attr('text-anchor', 'start').attr('font-size', 10).attr('font-weight', 700)
            .attr('fill', '#6b7280').attr('text-transform', 'uppercase').attr('letter-spacing', '0.05em')
            .text(lane.replace(/^_hdr_/, ''))
        } else {
          // Data lane
          g.append('rect').attr('x', margin.left).attr('y', laneY[lane])
            .attr('width', width - margin.left - margin.right).attr('height', laneHeight)
            .attr('fill', stripeIdx % 2 === 0 ? '#f9fafb' : '#ffffff').attr('stroke', '#e5e7eb').attr('stroke-width', 0.5)
          stripeIdx++
          if (isConditionAxis) {
            const spaceIdx = lane.indexOf(' ')
            const code = spaceIdx > 0 ? lane.slice(0, spaceIdx) : lane
            const desc = spaceIdx > 0 ? lane.slice(spaceIdx + 1) : ''
            const codeWidth = 60
            g.append('text').attr('x', 8).attr('y', y(lane)).attr('dy', 4)
              .attr('text-anchor', 'start').attr('font-size', 11).attr('font-weight', 600).attr('fill', '#7c3aed')
              .attr('font-family', 'monospace')
              .text(code)
            const descEl = g.append('text').attr('x', 8 + codeWidth).attr('y', y(lane)).attr('dy', 4)
              .attr('text-anchor', 'start').attr('font-size', 11).attr('fill', '#374151')
            const maxDescWidth = margin.left - codeWidth - 20
            const maxChars = Math.floor(maxDescWidth / 6)
            descEl.text(desc.length > maxChars ? desc.slice(0, maxChars - 1) + '…' : desc)
          } else {
            g.append('text').attr('x', margin.left - 8).attr('y', y(lane)).attr('dy', 4)
              .attr('text-anchor', 'end').attr('font-size', 11).attr('font-weight', 600).attr('fill', '#374151')
              .text(lane.length > 40 ? lane.slice(0, 38) + '…' : lane)
          }
        }
      })

      const axisG = g.append('g').attr('transform', `translate(0, ${margin.top - 5})`)
      axisG.call(d3.axisTop(x).ticks(8).tickFormat(d3.timeFormat('%b %Y')))
      axisG.selectAll('text').attr('font-size', 10).attr('fill', '#6b7280')
      axisG.selectAll('line, path').attr('stroke', '#d1d5db')

      const tooltip = d3.select(container).append('div').attr('class', 'graph-tooltip')

      // -- Provider color map --
      const provPalette = [
        '#2563eb','#dc2626','#059669','#d97706','#7c3aed','#0891b2','#ca8a04',
        '#9333ea','#be185d','#0d9488','#4f46e5','#15803d','#c2410c','#b91c1c',
        '#1d4ed8','#047857','#e11d48','#6d28d9','#0284c7','#7c2d12',
      ]
      const provColorMap = {}
      let provColorIdx = 0
      const getProviderName = (clm_id) => {
        const edges = (this.edgeIndex.byFrom['EOB_PRACTITIONER'] || {})[clm_id] || []
        if (!edges.length) return null
        const prac = this.nodeIndex.Practitioner?.[edges[0].to]
        return prac ? (prac.PROVIDER_NAME || edges[0].to) : edges[0].to
      }
      const getProvColor = (name) => {
        if (!name) return '#9ca3af'
        if (!provColorMap[name]) provColorMap[name] = provPalette[provColorIdx++ % provPalette.length]
        return provColorMap[name]
      }

      // Cost sizing
      const amounts = claimLanes.map(d => parseFloat(d.CLM_PMT_AMT) || 0).filter(a => a > 0)
      const costScale = amounts.length
        ? d3.scaleSqrt().domain([0, d3.max(amounts)]).range([3, 12])
        : null
      const dotR = (d) => costScale ? Math.max(3, costScale(parseFloat(d.CLM_PMT_AMT) || 0)) : 5

      // Condition color map
      const condPalette = [
        '#d97706','#dc2626','#2563eb','#059669','#7c3aed','#0891b2','#ca8a04',
        '#9333ea','#be185d','#0d9488','#4f46e5','#15803d','#c2410c','#b91c1c',
        '#1d4ed8','#047857','#e11d48','#6d28d9','#0284c7','#7c2d12',
      ]
      const condColorMap = {}
      let condColorIdx = 0
      const getCondColor = (clm_id) => {
        const edges = (this.edgeIndex.byFrom['EOB_CONDITION'] || {})[clm_id] || []
        if (!edges.length) return '#9ca3af'
        const code = edges[0].to
        if (!condColorMap[code]) condColorMap[code] = condPalette[condColorIdx++ % condPalette.length]
        return condColorMap[code]
      }

      const colorMode = this.timelineColor
      claimLanes.forEach((d, i) => {
        d._jitterY = (Math.sin(i * 7.3) * 0.5) * (laneHeight * 0.35)
        d._provName = getProviderName(d.CLM_ID)
        d._provColor = getProvColor(d._provName)
        if (colorMode === 'provider') d._dotColor = d._provColor
        else if (colorMode === 'condition') d._dotColor = getCondColor(d.CLM_ID)
        else d._dotColor = LANE_COLORS[d._laneKey] || LANE_COLORS[laneKey(d.CLM_TYPE_CD, d, this.edgeIndex, this.nodeIndex)] || '#6b7280'
      })

      g.append('g').selectAll('circle').data(claimLanes).join('circle')
        .attr('class', 'claim-dot')
        .attr('cx', d => x(d._date)).attr('cy', d => y(d._laneKey) + d._jitterY)
        .attr('r', d => dotR(d)).attr('fill', d => d._dotColor).attr('opacity', 0.8)
        .attr('stroke', '#fff').attr('stroke-width', 0.5).style('cursor', 'pointer')
        .on('mouseover', function (event, d) {
          const label = d.CLM_TYPE_LABEL || d.CLM_TYPE_CD || ''
          const amt = d.CLM_PMT_AMT
          const prov = d._provName || 'No provider'
          tooltip.style('opacity', 1)
            .html(`<strong>${label}</strong> ${d.CLM_FROM_DT || ''}<br/>${prov}<br/>${amt ? '$' + Number(amt).toLocaleString() : ''}<br/><span style="color:#9ca3af">${d._laneKey}</span>`)
          d3.select(this).attr('r', dotR(d) + 3).attr('opacity', 1)
        })
        .on('mousemove', (event) => {
          const containerWidth = container.clientWidth
          const tipWidth = 252
          let left = event.offsetX + 12
          if (left + tipWidth > containerWidth) left = event.offsetX - tipWidth - 4
          tooltip.style('left', left + 'px').style('top', (event.offsetY - 10) + 'px')
        })
        .on('mouseout', function (event, d) { tooltip.style('opacity', 0); d3.select(this).attr('r', dotR(d)).attr('opacity', 0.8) })
        .on('click', function (event, d) {
          g.selectAll('.claim-dot').attr('stroke', '#fff').attr('stroke-width', 0.5).each(function(dd) { d3.select(this).attr('r', dotR(dd)) })
          d3.select(this).attr('stroke', '#111827').attr('stroke-width', 2.5).attr('r', dotR(d) + 3)
          self.selectClaim(d)
        })

      // ── Clinical data lanes (below claims) ──
      const allObs = this.nodes.Observation || []
      const allMeds = this.nodes.MedicationStatement || []
      const allEnc = this.nodes.Encounter || []

      const claimLineByKey = Object.fromEntries((this.nodes.ClaimItem || []).map(l => [l._key, l]))
      const linesByClaim = {}
      for (const e of this.edges) {
        if (e.type !== 'CLAIM_ITEM') continue
        const line = claimLineByKey[e.to]
        if (line) (linesByClaim[e.from] ||= []).push(line)
      }

      // Build encounter→observations index from edges
      const encObsMap = {} // enc ENCOUNTER_KEY → [obs]
      const obsInEncounter = new Set() // obs _keys that belong to an encounter
      for (const e of this.edges) {
        if (e.type !== 'ENCOUNTER_OBSERVATION') continue
        if (!encObsMap[e.from]) encObsMap[e.from] = []
        const obs = this.nodeIndex.Observation?.[e.to]
        if (obs) {
          encObsMap[e.from].push(obs)
          obsInEncounter.add(e.to)
        }
      }

      // Enrich encounter dots with their observation counts
      const encDots = allEnc.map(enc => {
        const linked = encObsMap[enc.ENCOUNTER_KEY] || []
        const vitals = linked.filter(o => o.CATEGORY === 'vital-signs')
        const assessments = linked.filter(o => o.CATEGORY !== 'vital-signs' && o.CATEGORY !== 'laboratory')
        const labs = linked.filter(o => o.CATEGORY === 'laboratory')
        return { ...enc, _vitals: vitals, _assessments: assessments, _labs: labs, _linkedObs: linked,
                 _total: linked.length }
      })

      // Standalone labs — not linked to any encounter
      const standaloneLabs = allObs.filter(o => o.CATEGORY === 'laboratory' && !obsInEncounter.has(o._key))

      const clinicalDefs = [
        { key: 'Encounter', label: 'Encounters', color: '#6366f1', dateField: 'ENCOUNTER_DATE',
          items: encDots,
          tip: d => {
            const parts = [`<strong>${d.ENCOUNTER_TYPE || d.ENCOUNTER_CLASS || 'Encounter'}</strong>`]
            if (d.CLINICIAN) parts.push(d.CLINICIAN)
            if (d.LOCATION) parts.push(d.LOCATION)
            const counts = []
            if (d._vitals.length) counts.push(`${d._vitals.length} vitals`)
            if (d._assessments.length) counts.push(`${d._assessments.length} assessments`)
            if (d._labs.length) counts.push(`${d._labs.length} labs`)
            if (counts.length) parts.push(counts.join(' · '))
            return parts.join('<br/>')
          }},
        { key: 'Labs', label: 'Labs', color: '#0d9488', dateField: 'SERVICE_DATE',
          items: standaloneLabs,
          tip: d => `<strong>${d.CODE_DISPLAY || d.CODE || 'Lab'}</strong><br/>${d.VALUE_NUMERIC ? d.VALUE_NUMERIC + (d.UNITS ? ' ' + d.UNITS : '') : d.VALUE_STRING || ''}${d.ABNORMAL_FLAG ? '<br/><span style="color:#dc2626">⚠ ' + d.ABNORMAL_FLAG + '</span>' : ''}`
        },
        { key: 'Meds', label: 'Medications', color: '#8b5cf6', dateField: 'START_DATE',
          items: allMeds,
          tip: d => `<strong>${d.MED_DETAILS || d.NDC || 'Medication'}</strong>${d.FREQUENCY ? '<br/>' + d.FREQUENCY : ''}${d.STATUS ? '<br/>' + d.STATUS : ''}`
        },
        { key: 'Appointment', label: 'Appointments', color: '#f59e0b', dateField: 'APPOINTMENT_DATE',
          tip: d => `<strong>${d.APPT_TYPE || 'Visit'}</strong>${d.CLINICIAN ? '<br/>' + d.CLINICIAN : ''}${d.REASON ? '<br/>' + d.REASON : ''}`,
          items: (this.nodes.Appointment || []) },
        { key: 'EhrClaim', label: 'EHR Claims', color: '#64748b', dateField: 'SERVICE_DATE',
          tip: d => {
            const lines = linesByClaim[d.CLAIM_ID] || []
            const cpts = [...new Set(lines.map(l => l.CPT_CODE).filter(Boolean))]
            const parts = ['<strong>EHR claim</strong>']
            if (d.CLAIM_STATUS) parts.push(d.CLAIM_STATUS)
            if (cpts.length) parts.push(`CPT ${cpts.slice(0, 8).join(', ')}${cpts.length > 8 ? '…' : ''}`)
            if (lines.length) parts.push(`${lines.length} line${lines.length === 1 ? '' : 's'}`)
            return parts.join('<br/>')
          },
          items: (this.nodes.PreAdjClaim || []) },
        { key: 'ADTEpisode', label: 'ADT Episodes', color: '#dc2626', dateField: 'EPISODE_START',
          tip: d => {
            const parts = [`<strong>${d.EPISODE_TYPE || 'ADT'}</strong>`]
            if (d.EPISODE_START) parts.push(new Date(d.EPISODE_START).toLocaleString())
            if (d.FACILITY) parts.push(d.FACILITY)
            if (d.DURATION_HOURS && d.DURATION_HOURS !== '0') parts.push(`${d.DURATION_HOURS}h`)
            if (d.ADMIT_SOURCE) parts.push(`Admit: ${d.ADMIT_SOURCE}`)
            if (d.DISCHARGE_DISPOSITION) parts.push(`Dispo: ${d.DISCHARGE_DISPOSITION}`)
            return parts.join('<br/>')
          },
          items: (this.nodes.ADTEpisode || []) },
      ]
      const clinicalLanes = clinicalDefs.filter(def => def.items.length > 0)
      if (clinicalLanes.length) {
        const lastLane = lanes[lanes.length - 1]
        const clOffset = laneY[lastLane] + (groupHeaders.has(lastLane) ? headerHeight : laneHeight) + 12
        // Separator line
        g.append('line').attr('x1', margin.left).attr('x2', width - margin.right)
          .attr('y1', clOffset - 6).attr('y2', clOffset - 6)
          .attr('stroke', '#9ca3af').attr('stroke-dasharray', '4,3')

        const clLaneHeight = Math.max(24, Math.min(40, 160 / clinicalLanes.length))
        clinicalLanes.forEach((def, li) => {
          const items = def.items.map(d => ({
            ...d, _date: d._date || (def.dateField && d[def.dateField] ? new Date(d[def.dateField]) : null),
          })).filter(d => d._date && !isNaN(d._date))
          const cy = clOffset + li * clLaneHeight + clLaneHeight / 2

          // Lane background
          g.append('rect').attr('x', margin.left).attr('y', clOffset + li * clLaneHeight)
            .attr('width', width - margin.left - margin.right).attr('height', clLaneHeight)
            .attr('fill', li % 2 === 0 ? '#f0fdfa' : '#fdf4ff').attr('stroke', '#e5e7eb').attr('stroke-width', 0.5)
          // Lane label
          g.append('text').attr('x', margin.left - 8).attr('y', cy).attr('dy', 4)
            .attr('text-anchor', 'end').attr('font-size', 11).attr('font-weight', 600).attr('fill', def.color)
            .text(def.label)

          // Dots
          g.append('g').selectAll('circle').data(items).join('circle')
            .attr('class', 'clinical-dot')
            .attr('data-node-key', d => d._key || d.ENCOUNTER_KEY || '')
            .attr('data-lane', def.key)
            .attr('cx', d => x(d._date)).attr('cy', (d, i) => cy + (Math.sin(i * 7.3) * 0.4) * (clLaneHeight * 0.4))
            .attr('r', d => d._total ? Math.max(3, Math.min(8, Math.sqrt(d._total) * 1.2)) : 3).attr('fill', def.color).attr('opacity', 0.7)
            .attr('stroke', '#fff').attr('stroke-width', 0.5).style('cursor', 'pointer')
            .on('mouseover', function (event, d) {
              tooltip.style('opacity', 1).html(def.tip(d))
              d3.select(this).attr('r', 6).attr('opacity', 1)
            })
            .on('mousemove', (event) => {
              const containerWidth = container.clientWidth
              const tipWidth = 252 // max-width 240 + padding
              let left = event.offsetX + 12
              if (left + tipWidth > containerWidth) left = event.offsetX - tipWidth - 4
              tooltip.style('left', left + 'px').style('top', (event.offsetY - 10) + 'px')
            })
            .on('mouseout', function () { tooltip.style('opacity', 0); d3.select(this).attr('r', 3).attr('opacity', 0.7) })
            .on('click', function (event, d) {
              self.selectedClinicalItem = { ...d, _type: def.key, _label: def.label, _color: def.color }
              self.selectedClaim = null
            })
        })

        // Expand SVG height to fit clinical lanes
        const totalH = clOffset + clinicalLanes.length * clLaneHeight + 10
        svg.attr('height', Math.max(height, totalH))
      }

    },

    renderTreemap() {
      const container = this.$refs.researchContainer
      if (!container) return
      container.innerHTML = ''
      if (!this.claims.length) return

      const edgeIdx = this.edgeIndex
      const ndIdx = this.nodeIndex

      function subcat(claim) {
        const base = LANE_MAP[claim.CLM_TYPE_CD]
        const itemEdges = (edgeIdx.byFrom['EOB_ITEM'] || {})[claim.CLM_ID] || []
        if (base === '_PART_B') {
          const counts = {}
          for (const ie of itemEdges) {
            const item = ndIdx.Item?.[ie.to]
            if (!item) continue
            for (const ce of ((edgeIdx.byFrom['ITEM_HCPCS'] || {})[item._key] || [])) {
              const h = ndIdx.HCPCS_CODE?.[ce.to]
              const sc = h?.RBCS_SUBCAT_DESC
              if (sc) counts[sc] = (counts[sc] || 0) + 1
            }
          }
          const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
          return best ? best[0] : null
        }
        if (base === '_OUTPATIENT') {
          const counts = {}
          for (const ie of itemEdges) {
            const item = ndIdx.Item?.[ie.to]
            if (!item) continue
            for (const ce of ((edgeIdx.byFrom['ITEM_REV_CTR'] || {})[item._key] || [])) {
              const r = ndIdx.REV_CTR_CODE?.[ce.to]
              const cat = r?.CATEGORY
              if (cat) counts[cat] = (counts[cat] || 0) + 1
            }
          }
          const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
          return best ? best[0] : null
        }
        return null
      }

      const items = []
      for (const c of this.claims) {
        const lane = laneKey(c.CLM_TYPE_CD, c, edgeIdx, ndIdx)
        const sc = subcat(c) || 'General'
        const pracEdges = (edgeIdx.byFrom['EOB_PRACTITIONER'] || {})[c.CLM_ID] || []
        const prac = pracEdges.length ? (ndIdx.Practitioner?.[pracEdges[0].to] || {}) : {}
        const provName = prac.PROVIDER_NAME || prac.NPI || ''
        const itemEdges = (edgeIdx.byFrom['EOB_ITEM'] || {})[c.CLM_ID] || []
        for (const ie of itemEdges) {
          const item = ndIdx.Item?.[ie.to]
          if (!item) continue
          const hcpcsEdges = (edgeIdx.byFrom['ITEM_HCPCS'] || {})[item._key] || []
          const hcpcs = hcpcsEdges.length ? ndIdx.HCPCS_CODE?.[hcpcsEdges[0].to] : null
          const revEdges = (edgeIdx.byFrom['ITEM_REV_CTR'] || {})[item._key] || []
          const rev = revEdges.length ? ndIdx.REV_CTR_CODE?.[revEdges[0].to] : null
          const code = hcpcs?.CODE || item.CODE || rev?.CODE || ''
          const desc = hcpcs?.DESCRIPTION || rev?.DESCRIPTION || ''
          const amt = parseFloat(item.PAID_AMT || item.ALLOWED_AMT || 0)
          const label = [code, desc].filter(Boolean).join(' — ')
          items.push({ lane, sc, provName, code, label: label || 'Unknown', amt, date: c.CLM_FROM_DT || '', _claim: c })
        }
      }

      // Group: lane → subcat → HCPCS code → line items
      const treeData = {}
      for (const it of items) {
        if (!treeData[it.lane]) treeData[it.lane] = {}
        if (!treeData[it.lane][it.sc]) treeData[it.lane][it.sc] = {}
        const key = it.code || 'Unknown'
        if (!treeData[it.lane][it.sc][key]) treeData[it.lane][it.sc][key] = { label: it.label, lines: [] }
        treeData[it.lane][it.sc][key].lines.push(it)
      }

      const fmt$ = d3.format('$,.0f')

      const data = {
        name: this.patientName || this.patientIdentifier,
        children: Object.entries(treeData).map(([lane, subcats]) => ({
          name: lane,
          children: Object.entries(subcats).map(([sc, codes]) => ({
            name: sc,
            children: Object.entries(codes).map(([code, agg]) => ({
              name: agg.label,
              _amt: agg.lines.reduce((s, l) => s + l.amt, 0),
              children: agg.lines.map(it => ({
                name: `${it.date}  ${fmt$(it.amt)}${it.provName ? '  · ' + it.provName : ''}`,
                _amt: it.amt,
                _claim: it._claim,
              })),
            })),
          })),
        })),
      }

      // Compute aggregate _amt up the tree
      function sumAmt(node) {
        if (!node.children) return node._amt || 0
        node._amt = node.children.reduce((s, c) => s + sumAmt(c), 0)
        return node._amt
      }
      sumAmt(data)

      const root = d3.hierarchy(data)
      // Collapse: only root's children visible initially
      root.children?.forEach(function collapse(d) {
        if (d.children) {
          d._children = d.children
          d._children.forEach(collapse)
          d.children = null
        }
      })

      const pillPad = 4
      const pillRx = 4

      const color = (d) => {
        let n = d
        while (n.depth > 1 && n.parent) n = n.parent
        return LANE_COLORS[n.data.name] || DIM_PALETTE[(n.parent?.children?.indexOf(n) || 0) % DIM_PALETTE.length] || '#6b7280'
      }

      const width = container.clientWidth || 800
      const height = container.clientHeight || 600

      const pad = 10

      const svg = d3.select(container).append('svg')
        .attr('width', width)
        .attr('height', height)
        .style('font', '11px Inter, system-ui, sans-serif')

      const g = svg.append('g').attr('transform', `translate(${pad},${pad})`)

      const pillH = 24
      const colGap = 40

      function update(source) {
        const nodes = root.descendants().filter(d => d.depth > 0)

        // Group by depth into columns
        const byCol = {}
        for (const d of nodes) {
          const col = d.depth - 1
          if (!byCol[col]) byCol[col] = []
          byCol[col].push(d)
        }

        // Measure widest label per column (name + amount)
        const tempText = svg.append('text').attr('font-size', '11px').style('visibility', 'hidden')
        const colWidths = {}
        for (const [colIdx, col] of Object.entries(byCol)) {
          let maxW = 0
          for (const d of col) {
            const label = `${(d.data.name || '').slice(0, 50)}  ${fmt$(d.data._amt || 0)}`
            tempText.text(label)
            maxW = Math.max(maxW, tempText.node().getComputedTextLength() + 16)
          }
          colWidths[colIdx] = maxW
        }
        tempText.remove()

        // Position: columns left-to-right, each column width = its widest pill
        let colX = 0
        for (const [colIdx, col] of Object.entries(byCol)) {
          const w = colWidths[colIdx]
          col.forEach((node, i) => {
            node._pillW = w
            node._pillH = pillH
            node._tx = colX
            node._ty = i * (pillH + pillPad)
          })
          colX += w + colGap
        }

        const links = root.links().filter(l => l.source.depth > 0)
        const duration = 400

        // Links — horizontal bezier from pill right to child pill left
        const linkPath = (d) => {
          const sx = d.source._tx + (d.source._pillW || 60)
          const sy = d.source._ty + pillH / 2
          const tx = d.target._tx
          const ty = d.target._ty + pillH / 2
          const mx = (sx + tx) / 2
          return `M${sx},${sy} C${mx},${sy} ${mx},${ty} ${tx},${ty}`
        }

        const linkCollapsed = (s) => {
          const sx = s._tx + (s._pillW || 60)
          const sy = s._ty + pillH / 2
          return `M${sx},${sy} C${sx},${sy} ${sx},${sy} ${sx},${sy}`
        }

        const link = g.selectAll('path.col-link')
          .data(links, d => d.target.data.name + d.target.depth)

        link.enter().append('path')
          .attr('class', 'col-link')
          .attr('fill', 'none')
          .attr('stroke', d => color(d.target))
          .attr('stroke-opacity', 0.25)
          .attr('stroke-width', 1.5)
          .attr('d', () => linkCollapsed(source))
          .merge(link)
          .transition().duration(duration)
          .attr('d', linkPath)
          .attr('stroke', d => color(d.target))

        link.exit().transition().duration(duration)
          .attr('d', () => linkCollapsed(source))
          .remove()

        // Nodes
        const node = g.selectAll('g.col-node')
          .data(nodes, d => d.data.name + d.depth)

        const nodeEnter = node.enter().append('g')
          .attr('class', 'col-node')
          .attr('transform', `translate(${source._tx || 0},${source._ty || 0})`)
          .style('cursor', d => (d.children || d._children) ? 'pointer' : 'default')
          .on('click', (event, d) => {
            if (d.children) {
              d._children = d.children
              d.children = null
            } else if (d._children) {
              d.children = d._children
              d._children = null
            }
            update(d)
          })

        nodeEnter.append('rect')
          .attr('width', d => d._pillW || 60)
          .attr('height', pillH)
          .attr('rx', pillRx)
          .attr('fill', d => color(d))
          .attr('fill-opacity', d => d._children ? 1 : 0.8)

        nodeEnter.append('clipPath')
          .attr('id', (d, i) => `rclip-${d.depth}-${i}`)
          .append('rect')
            .attr('width', d => d._pillW || 60)
            .attr('height', pillH)

        const nodeText = nodeEnter.append('text')
          .attr('clip-path', (d, i) => `url(#rclip-${d.depth}-${i})`)
          .attr('x', 6)
          .attr('y', 15)
          .attr('fill', '#fff')
          .attr('font-size', '11px')
          .style('pointer-events', 'none')

        nodeText.append('tspan')
          .attr('font-weight', 500)
          .text(d => d.data.name || '')

        nodeText.append('tspan')
          .attr('fill-opacity', 0.7)
          .attr('dx', 6)
          .text(d => fmt$(d.data._amt || 0))

        nodeEnter.append('title')
          .text(d => `${d.data.name}\n${fmt$(d.data._amt || 0)}`)

        // Update
        node.merge(nodeEnter).transition().duration(duration)
          .attr('transform', d => `translate(${d._tx},${d._ty})`)

        node.merge(nodeEnter).select('rect')
          .attr('fill-opacity', d => d._children ? 1 : 0.8)

        // Exit
        node.exit().transition().duration(duration)
          .attr('transform', `translate(${source._tx || 0},${source._ty || 0})`)
          .attr('opacity', 0)
          .remove()

        nodes.forEach(d => { d._tx0 = d._tx; d._ty0 = d._ty })

        // Resize SVG to fit content
        const maxY = (d3.max(nodes, d => d._ty + pillH) || 0) + pad * 2
        const maxX = (d3.max(nodes, d => d._tx + (d._pillW || 60)) || 0) + pad * 2
        svg.attr('width', Math.max(width, maxX)).attr('height', Math.max(height, maxY))
      }

      root._tx = 0
      root._ty = 0
      root._pillW = 0
      root._pillH = 0
      update(root)
    },

    renderGraph() {
      const container = this.$refs.graphContainer
      if (!container) return
      container.innerHTML = ''
      if (!this.claims.length) return

      // Kill previous simulation
      if (this._graphSim) { this._graphSim.stop(); this._graphSim = null }

      const width = container.clientWidth || 800
      const height = container.clientHeight || 600

      // -- Build ring map from graphLayers order (Patient is always ring 0) --
      const visibleTypes = new Set(this.graphLayers.filter(l => l.visible).map(l => l.id))
      const ringMap = { Patient: 0 }
      const colorMap = { Patient: '#111827' }
      const radiusMap = { Patient: 16, Claim: 4, Item: 3, Practitioner: 8, Organization: 8, Condition: 5 }
      let ring = 1
      for (const layer of this.graphLayers) {
        if (layer.visible) {
          ringMap[layer.id] = ring++
          colorMap[layer.id] = layer.color
        }
      }

      const gNodes = []
      const gLinks = []
      const nodeMap = {}

      // Patient (always present)
      const pat = (this.nodes.Patient || [])[0]
      if (!pat) return
      const mbi = pat.MBI || this.patientIdentifier
      const patId = `Patient:${mbi}`
      const patNode = { id: patId, type: 'Patient', label: `${pat.FIRST_NAME || ''} ${pat.LAST_NAME || ''}`.trim() || mbi, data: pat }
      gNodes.push(patNode)
      nodeMap[patId] = patNode

      // Claims
      const claims = this.nodes.Claim || []
      const MAX_CLAIMS = 200
      const MAX_ITEMS = 500
      const showClaims = claims.slice(0, MAX_CLAIMS)
      const claimsVisible = visibleTypes.has('Claim')
      for (const c of showClaims) {
        const cid = c.CLM_ID
        const uid = `Claim:${cid}`
        const label = `${laneKey(c.CLM_TYPE_CD, c, this.edgeIndex, this.nodeIndex)} ${c.CLM_FROM_DT || ''}`.trim()
        const n = { id: uid, type: 'Claim', label, data: c, _hidden: !claimsVisible }
        gNodes.push(n)
        nodeMap[uid] = n
        if (claimsVisible) {
          gLinks.push({ source: patId, target: uid, type: 'PATIENT_EOB' })
        }
      }
      const shownClaimIds = new Set(showClaims.map(c => c.CLM_ID))

      // Helper: add a linked node type
      const addLinked = (nodeType, keyField, edgeType, maxCount) => {
        if (!visibleTypes.has(nodeType)) return
        const items = this.nodes[nodeType] || []
        let count = 0
        for (const item of items) {
          const key = item[keyField]
          if (!key) continue
          const uid = `${nodeType}:${key}`
          if (!nodeMap[uid]) {
            nodeMap[uid] = { id: uid, type: nodeType, label: key, data: item }
            gNodes.push(nodeMap[uid])
          }
          count++
          if (maxCount && count > maxCount) break
        }
        for (const e of this.edges) {
          if (e.type !== edgeType) continue
          if (!shownClaimIds.has(e.from)) continue
          const srcId = `Claim:${e.from}`
          const tgtId = `${nodeType}:${e.to}`
          if (!nodeMap[srcId] || !nodeMap[tgtId]) continue
          if (claimsVisible) {
            gLinks.push({ source: srcId, target: tgtId, type: edgeType })
          } else {
            gLinks.push({ source: patId, target: tgtId, type: edgeType })
          }
        }
      }

      addLinked('Practitioner', 'NPI', 'EOB_PRACTITIONER')
      addLinked('Organization', 'ORG_ID', 'EOB_ORGANIZATION')
      addLinked('Condition', 'CODE', 'EOB_CONDITION')
      addLinked('Item', '_key', 'EOB_ITEM', MAX_ITEMS)

      // Filter out hidden claim nodes
      const visibleNodes = gNodes.filter(n => !n._hidden)

      // Label builders per type
      const labelOf = (d) => {
        switch (d.type) {
          case 'Patient': return d.label
          case 'Practitioner': return d.data.PROVIDER_NAME || d.data.NPI || ''
          case 'Organization': return d.data.FACILITY_NAME || d.data.ORG_ID || ''
          case 'Claim': return d.label
          case 'Condition': return d.data.CODE || ''
          case 'Item': return d.data.CODE || ''
          default: return ''
        }
      }

      // -- D3 force simulation --
      const maxRing = ring
      const ringSpacing = Math.min(120, (Math.min(width, height) * 0.4) / Math.max(maxRing, 1))
      const getRing = (d) => ringMap[d.type] ?? 1

      let claimSizeScale = null
      if (this.sizeByPaid) {
        const amounts = showClaims.map(c => parseFloat(c.CLM_PMT_AMT) || 0).filter(a => a > 0)
        if (amounts.length) {
          claimSizeScale = d3.scaleSqrt().domain([0, d3.max(amounts)]).range([3, 20])
        }
      }
      const getR = (d) => {
        if (d.type === 'Claim' && claimSizeScale) return claimSizeScale(parseFloat(d.data.CLM_PMT_AMT) || 0)
        return radiusMap[d.type] ?? 4
      }

      const visibleIds = new Set(visibleNodes.map(n => n.id))
      const visibleLinks = gLinks.filter(l => {
        const sId = typeof l.source === 'object' ? l.source.id : l.source
        const tId = typeof l.target === 'object' ? l.target.id : l.target
        return visibleIds.has(sId) && visibleIds.has(tId)
      })

      const simulation = d3.forceSimulation(visibleNodes)
        .force('link', d3.forceLink(visibleLinks).id(d => d.id).distance(d => {
          const src = typeof d.source === 'object' ? d.source : nodeMap[d.source]
          const tgt = typeof d.target === 'object' ? d.target : nodeMap[d.target]
          if (!src || !tgt) return 80
          return 30 + Math.abs(getRing(src) - getRing(tgt)) * ringSpacing * 0.5
        }).strength(0.3))
        .force('charge', d3.forceManyBody().strength(d => d.type === 'Patient' ? -400 : getR(d) <= 3 ? -5 : -30))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('radial', d3.forceRadial(d => getRing(d) * ringSpacing, width / 2, height / 2).strength(0.4))
        .force('collide', d3.forceCollide(d => getR(d) + 2))
        .alphaDecay(0.03)

      this._graphSim = simulation

      // -- SVG --
      const svg = d3.select(container).append('svg')
        .attr('width', width).attr('height', height)

      const gRoot = svg.append('g')
      svg.call(d3.zoom()
        .scaleExtent([0.1, 5])
        .on('zoom', (event) => gRoot.attr('transform', event.transform))
      )

      // Links
      const link = gRoot.append('g')
        .selectAll('line').data(visibleLinks).join('line')
        .attr('stroke', '#e5e7eb').attr('stroke-width', 0.5).attr('stroke-opacity', 0.6)

      // Nodes
      const node = gRoot.append('g')
        .selectAll('circle').data(visibleNodes).join('circle')
        .attr('class', d => d.type === 'Claim' ? 'graph-node graph-claim' : 'graph-node')
        .attr('r', d => getR(d))
        .attr('fill', d => {
          if (d.type === 'Claim') return LANE_COLORS[laneKey(d.data.CLM_TYPE_CD, d.data, this.edgeIndex, this.nodeIndex)] || '#6b7280'
          return colorMap[d.type] || '#6b7280'
        })
        .attr('stroke', '#fff').attr('stroke-width', d => d.type === 'Patient' ? 2 : 0.5)
        .attr('opacity', d => getR(d) <= 3 ? 0.5 : 0.85)
        .style('cursor', 'pointer')
        .call(d3.drag()
          .on('start', (event, d) => { if (!event.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y })
          .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y })
          .on('end', (event, d) => { if (!event.active) simulation.alphaTarget(0); d.fx = null; d.fy = null })
        )

      // Labels (only for larger nodes)
      const labels = gRoot.append('g')
        .selectAll('text').data(visibleNodes.filter(d => getR(d) >= 5))
        .join('text')
        .text(d => {
          const l = labelOf(d)
          return l.length > 20 ? l.slice(0, 18) + '…' : l
        })
        .attr('font-size', d => d.type === 'Patient' ? 11 : 9)
        .attr('font-weight', d => d.type === 'Patient' ? 700 : 500)
        .attr('fill', '#374151')
        .attr('text-anchor', 'middle')
        .attr('dy', d => getR(d) + 12)
        .attr('pointer-events', 'none')

      // Tooltip
      const tooltip = d3.select(container).append('div').attr('class', 'graph-tooltip')

      node.on('mouseover', (event, d) => {
          const l = labelOf(d)
          const extra = d.type === 'Claim' ? `<br/>$${Number(d.data.CLM_PMT_AMT || 0).toLocaleString()}` : ''
          tooltip.style('opacity', 1).html(`<strong>${d.type}</strong><br/>${l}${extra}`)
          d3.select(event.target).attr('opacity', 1).attr('stroke', '#111827').attr('stroke-width', 2)
        })
        .on('mousemove', (event) => {
          const [x, y] = d3.pointer(event, container)
          tooltip.style('left', (x + 12) + 'px').style('top', (y - 10) + 'px')
        })
        .on('mouseout', (event, d) => {
          tooltip.style('opacity', 0)
          d3.select(event.target)
            .attr('opacity', getR(d) <= 3 ? 0.5 : 0.85)
            .attr('stroke', '#fff')
            .attr('stroke-width', d.type === 'Patient' ? 2 : 0.5)
        })
        .on('click', (event, d) => {
          if (d.type === 'Claim') this.selectClaim(d.data)
        })

      // Legend (fixed position, not affected by zoom)
      const legendItems = []
      // Claim service lines present in data
      const seenLanes = new Set()
      for (const c of showClaims) {
        seenLanes.add(laneKey(c.CLM_TYPE_CD, c, this.edgeIndex, this.nodeIndex))
      }
      for (const [lane, color] of Object.entries(LANE_COLORS)) {
        if (seenLanes.has(lane)) legendItems.push({ label: lane, color })
      }
      // Non-claim node types
      for (const layer of this.graphLayers) {
        if (layer.visible && layer.id !== 'Claim') {
          legendItems.push({ label: layer.label, color: layer.color })
        }
      }
      legendItems.unshift({ label: 'Patient', color: '#111827' })

      const legendG = svg.append('g')
        .attr('transform', `translate(${width - 14}, ${height - legendItems.length * 18 - 8})`)
      legendItems.forEach((item, i) => {
        const row = legendG.append('g').attr('transform', `translate(0, ${i * 18})`)
        row.append('circle').attr('cx', 0).attr('cy', 0).attr('r', 5).attr('fill', item.color)
        row.append('text').attr('x', -10).attr('y', 4).attr('text-anchor', 'end')
          .attr('font-size', 10).attr('fill', '#6b7280').text(item.label)
      })

      // Tick
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x).attr('y2', d => d.target.y)
        node.attr('cx', d => d.x).attr('cy', d => d.y)
        labels.attr('x', d => d.x).attr('y', d => d.y)
      })
    },
  },
}
</script>

<style scoped>
.subgraph-tab { display: flex; flex-direction: column; height: 100%; }
.loading-state { padding: 40px; text-align: center; color: #6b7280; font-size: 13px; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
.error-banner { background: #FEE2E2; color: #991B1B; padding: 8px 12px; font-size: 13px; flex-shrink: 0; }
.map-body { flex: 1; display: flex; gap: 0; min-height: 0; overflow: hidden; }

/* ---- Left sidebar ---- */
.left-sidebar { width: 240px; min-width: 240px; border-right: 1px solid #e5e7eb; background: #fff; display: flex; flex-direction: column; flex-shrink: 0; overflow: hidden; transition: width 0.15s ease, min-width 0.15s ease; position: relative; }
.left-sidebar.collapsed { width: 32px; min-width: 32px; }
.sidebar-toggle { position: absolute; top: 8px; right: 8px; width: 20px; height: 20px; border: none; background: none; color: #9ca3af; cursor: pointer; font-size: 10px; display: flex; align-items: center; justify-content: center; border-radius: 3px; z-index: 2; }
.sidebar-toggle:hover { background: #f3f4f6; color: #374151; }
.left-sidebar.collapsed .sidebar-toggle { right: auto; left: 6px; top: 8px; }

.sidebar-view-toggle { display: flex; gap: 2px; padding: 10px 10px 0; }
.sidebar-view-toggle.inline-toggle { padding: 0; flex-shrink: 0; }
.view-btn { padding: 5px 10px; font-size: 11px; font-weight: 600; border: 1px solid #e5e7eb; background: #fff; color: #6b7280; cursor: pointer; border-radius: 5px; transition: all 0.1s; display: flex; align-items: center; justify-content: center; gap: 4px; white-space: nowrap; }
.view-btn:hover { border-color: #7c3aed; color: #7c3aed; }
.view-btn.active { background: #7c3aed; color: #fff; border-color: #7c3aed; }

.sidebar-stats { padding: 8px 10px 0; font-size: 10px; color: #9ca3af; }

.sidebar-section { padding: 8px 10px; }
.episode-btn { width: 100%; padding: 6px 10px; background: #7c3aed; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 11px; font-weight: 500; display: flex; align-items: center; justify-content: center; gap: 5px; }
.episode-btn:hover { background: #6d28d9; }
.status-text { color: #6b7280; font-size: 11px; }
.status-text.loading { color: #7c3aed; font-weight: 500; }

.sidebar-episodes { flex: 1; overflow-y: auto; border-top: 1px solid #f3f4f6; }
.sidebar-header { padding: 8px 10px; font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #f3f4f6; position: sticky; top: 0; background: #fff; z-index: 1; display: flex; align-items: center; justify-content: space-between; }
.clear-btn { font-size: 10px; color: #9ca3af; background: none; border: none; cursor: pointer; padding: 0; }
.clear-btn:hover { color: #374151; }

.episode-item { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; cursor: pointer; transition: background 0.1s; }
.episode-item:hover { background: #f9fafb; }
.episode-item.active { background: #f3f0ff; }
.episode-item.dimmed { opacity: 0.35; }
.episode-item-header { display: flex; align-items: center; gap: 5px; }
.episode-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.episode-item-name { font-size: 11px; font-weight: 600; color: #111827; line-height: 1.4; margin-top: 3px; }
.episode-severity-pip { width: 16px; height: 16px; border-radius: 50%; font-size: 9px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.episode-severity-pip.high { background: #FEE2E2; color: #991B1B; }
.episode-severity-pip.medium { background: #FEF3C7; color: #92400E; }
.episode-severity-pip.low { background: #D1FAE5; color: #065F46; }
.episode-item-meta { font-size: 10px; color: #6b7280; margin-top: 2px; }
.episode-item-dates { font-size: 10px; color: #9ca3af; margin-top: 1px; }


/* ---- Main area ---- */
.main-area { flex: 1; min-width: 0; display: flex; flex-direction: column; overflow: hidden; position: relative; }

.tab-controls { display: flex; align-items: center; gap: 8px; padding: 6px 12px; border-bottom: 1px solid #f3f4f6; flex-shrink: 0; }
.axis-controls { display: flex; gap: 16px; }
.axis-group { display: flex; align-items: center; gap: 6px; }
.axis-label { font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; }
.dimension-pills { display: flex; gap: 3px; flex-wrap: wrap; }
.dim-pill { padding: 2px 8px; font-size: 11px; border: 1px solid #d1d5db; border-radius: 10px; background: #fff; color: #374151; cursor: pointer; transition: all 0.1s; white-space: nowrap; }
.dim-pill:hover { border-color: #7c3aed; color: #7c3aed; }
.dim-pill.active { background: #7c3aed; color: #fff; border-color: #7c3aed; }
.dim-pill.disabled { opacity: 0.3; cursor: default; pointer-events: none; }

.timeline-area { flex: 1; min-width: 0; display: flex; flex-direction: column; overflow: hidden; position: relative; }
.timeline-container { width: 100%; flex: 1 1 0; min-height: 500px; background: #fff; position: relative; overflow: auto; }
.timeline-container :deep(.graph-tooltip) { position: absolute; background: #1f2937; color: #fff; padding: 6px 10px; border-radius: 6px; font-size: 12px; pointer-events: none; opacity: 0; z-index: 10; max-width: 240px; line-height: 1.4; }

.graph-view { flex: 1; min-width: 0; min-height: 0; overflow: hidden; position: relative; background: #fafafa; }
.graph-container { width: 100%; height: 100%; }
.research-view { flex: 1; min-width: 0; min-height: 0; overflow: auto; position: relative; }
.research-container { width: 100%; height: 100%; position: relative; }
.graph-view :deep(.graph-tooltip) { position: absolute; background: #1f2937; color: #fff; padding: 6px 10px; border-radius: 6px; font-size: 12px; pointer-events: none; opacity: 0; z-index: 10; max-width: 240px; line-height: 1.4; }

/* Layer toolbar (in flow, above graph) */
.layer-bar { display: flex; align-items: center; gap: 6px; padding: 6px 12px; border-bottom: 1px solid #f3f4f6; flex-shrink: 0; flex-wrap: wrap; background: #fff; }
.layer-bar-label { font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-right: 4px; }
.layer-chip { display: flex; align-items: center; gap: 4px; padding: 3px 8px; font-size: 11px; border: 1px solid #d1d5db; border-radius: 10px; cursor: pointer; transition: all 0.1s; user-select: none; }
.layer-chip:hover { border-color: #7c3aed; }
.layer-chip.off { opacity: 0.35; border-style: dashed; }
.layer-bar-sep { width: 1px; height: 18px; background: #e5e7eb; flex-shrink: 0; }
.layer-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.layer-chip-count { color: #9ca3af; font-size: 10px; }
.arrow-btn { background: none; border: none; color: #9ca3af; cursor: pointer; padding: 0 1px; font-size: 9px; line-height: 1; }
.arrow-btn:hover:not(:disabled) { color: #374151; }
.arrow-btn:disabled { opacity: 0.2; cursor: default; }

/* ---- Claim drawer (right) ---- */
.claim-drawer { width: 320px; min-width: 320px; border-left: 1px solid #e5e7eb; background: #fff; display: flex; flex-direction: column; overflow-y: auto; }
.drawer-enter-active, .drawer-leave-active { transition: width 0.2s ease, min-width 0.2s ease, opacity 0.2s ease; }
.drawer-enter-from, .drawer-leave-to { width: 0; min-width: 0; opacity: 0; }
.drawer-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; }
.drawer-type-badge { color: #fff; font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 4px; margin-right: 6px; }
.drawer-date { font-size: 12px; color: #6b7280; }
.drawer-close { background: none; border: none; font-size: 16px; cursor: pointer; color: #9ca3af; padding: 0 4px; }
.drawer-close:hover { color: #374151; }
.drawer-content { padding: 0; }
.drawer-section { padding: 10px 14px; border-bottom: 1px solid #f3f4f6; }
.drawer-section h4 { margin: 0 0 6px 0; font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
.drawer-row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 12px; }
.drawer-label { color: #6b7280; }
.drawer-value { color: #111827; font-weight: 500; }
.drawer-card { background: #f9fafb; border-radius: 5px; padding: 6px 8px; margin-bottom: 4px; }
.drawer-card.compact { display: flex; gap: 6px; align-items: baseline; padding: 5px 8px; }
.card-title { font-size: 12px; font-weight: 600; color: #111827; display: flex; justify-content: space-between; }
.card-subtitle { font-size: 11px; color: #6b7280; margin-top: 1px; }
.item-amt { font-weight: 400; color: #059669; }
.dx-code { font-family: monospace; font-size: 11px; font-weight: 600; color: #7c3aed; white-space: nowrap; }
.dx-desc { font-size: 11px; color: #374151; }
.mono { font-family: monospace; }
.ep-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 3px; vertical-align: middle; }

/* Episode overlay (bottom of timeline area) */
.episode-overlay { position: absolute; bottom: 0; left: 0; right: 0; background: rgba(255,255,255,0.97); border-top: 1px solid #e5e7eb; box-shadow: 0 -2px 8px rgba(0,0,0,0.06); max-height: 220px; overflow-y: auto; z-index: 4; }
.episode-overlay-header { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; font-size: 11px; font-weight: 600; color: #6b7280; border-bottom: 1px solid #f3f4f6; position: sticky; top: 0; background: rgba(255,255,255,0.97); }
.episode-overlay-close { background: none; border: none; font-size: 14px; cursor: pointer; color: #9ca3af; padding: 0 4px; }
.episode-overlay-close:hover { color: #374151; }
.episode-overlay-body { padding: 8px 12px; display: flex; flex-direction: column; gap: 10px; }
.episode-overlay-card { border-left: 3px solid #e5e7eb; padding-left: 10px; }
.episode-overlay-card-header { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.episode-overlay-card-label { font-size: 12px; font-weight: 600; color: #111827; }
.episode-overlay-card-stats { font-size: 10px; color: #9ca3af; margin-left: auto; }
.episode-overlay-card-desc { font-size: 11px; color: #374151; line-height: 1.5; margin: 4px 0 0; }
.episode-severity-badge { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 1px 6px; border-radius: 4px; }
.episode-severity-badge.high { background: #FEE2E2; color: #991B1B; }
.episode-severity-badge.medium { background: #FEF3C7; color: #92400E; }
.episode-severity-badge.low { background: #D1FAE5; color: #065F46; }
.episode-detail-slide-enter-active, .episode-detail-slide-leave-active { transition: all 0.15s ease; overflow: hidden; }
.episode-detail-slide-enter-from, .episode-detail-slide-leave-to { opacity: 0; max-height: 0; }
.top-controls { display: flex; align-items: center; gap: 12px; padding: 6px 12px; border-bottom: 1px solid #f3f4f6; flex-shrink: 0; flex-wrap: wrap; }
.top-stats { font-size: 10px; color: #9ca3af; margin-left: auto; white-space: nowrap; }
.below-swimlanes { padding: 8px 12px; border-top: 1px solid #f3f4f6; flex-shrink: 0; }
.episodes-below { border-top: 1px solid #e5e7eb; flex-shrink: 0; overflow-y: auto; max-height: 300px; }
.episodes-below-header { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-bottom: 1px solid #f3f4f6; position: sticky; top: 0; background: #fff; z-index: 2; }
.episodes-below-title { font-size: 11px; font-weight: 600; color: #374151; }
.episodes-below-list { display: flex; flex-direction: column; gap: 0; }
.episode-card { padding: 10px 14px; border-bottom: 1px solid #f3f4f6; cursor: pointer; transition: background 0.1s; }
.episode-card:hover { background: #f9fafb; }
.episode-card.active { background: #f3f0ff; }
.episode-card.dimmed { opacity: 0.35; }
.episode-card-top { display: flex; align-items: center; gap: 6px; }
.episode-card-label { font-size: 12px; font-weight: 600; color: #111827; }
.episode-card-desc { font-size: 11px; color: #374151; line-height: 1.5; margin: 4px 0 0; }
.episode-card-meta { font-size: 10px; color: #9ca3af; margin-top: 3px; }
</style>
